import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageWrapper from "../components/PageWrapper";
import { useSiteContext } from "../SiteContext";
import * as API from "../api/timeOffRequests";
import {
  TimeOffRequestSummary,
  TimeOffRequestDetail,
  TimeOffRequestDetailItem,
  TimeOffRequestFilters,
  TimeOffApprovalAction,
  CalendarDay,
  CalendarHoliday,
  CalendarRequestEntry,
  CalendarDayRequest,
  CashOutRequestSummary,
  TimeOffRequestStatus,
} from "../interfaces/TimeOffRequest";
import { getEmployeeTimeOffBalance } from "../api/timeOff";
import { EmployeeTimeOffBalanceRow } from "../interfaces/TimeOff";
import TimeOffBalancesTable, { fmtHrs } from "../components/TimeOffBalancesTable";
import { can } from "../util/adminPermissions";
import {
  BulkApproveFailure,
  UNPAID_DAY_HOURS,
  blockingOf,
  describeBlocking,
  isPaidNotExhaustedError,
  isUnpaid,
  notRequestableText,
  refusalMessageOf,
  summarizeBulkApproveFailures,
} from "../util/timeOffUnpaid";
import { getEmployeeHolidays } from "../api/timesheetReview";
import config from "../configLoader";
import {
  AlertTriangle,
  Calendar,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  DollarSign,
  Inbox,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Wallet,
  X,
  XCircle,
} from "lucide-react";
import "./TimeOffRequestApprovalPage.css";

// ─── Types ───────────────────────────────────────────────────────────────────

type MainTab = "queue" | "calendar";
type QueueSubTab = "timeOff" | "cashOut";

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Would the API let this admin approve/deny the request? The server sends `canAct` per request;
 * an older API omits it, so a missing flag means "actionable" and the buttons stay visible.
 */
const canActOn = (r: { canAct?: boolean }): boolean => r.canAct !== false;

/**
 * Would the API let this admin correct the request AFTER approval? Same tolerance as `canActOn`:
 * an older API omits `canEditApproved`, and a missing flag must not hide the buttons — the
 * client-side `editApprovedTimeOffRequests` check is applied alongside this, and the server
 * re-checks both the permission and scope regardless.
 */
const canEditApprovedOn = (r: { canEditApproved?: boolean }): boolean => r.canEditApproved !== false;

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0]?.slice(0, 2) ?? "?").toUpperCase();
}

/** "Hector Diaz" → "Hector", for copy that addresses the employee by name. */
function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

function fmtDate(iso: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${m}/${d}/${y}`;
}

const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** "2026-09-08" → "Sep 8", for prose that names a day inside a sentence. */
function fmtShortDate(iso: string): string {
  if (!iso) return "";
  const [, m, d] = iso.slice(0, 10).split("-");
  const month = MONTH_ABBR[Number(m) - 1];
  if (!month) return iso.slice(0, 10);
  return `${month} ${Number(d)}`;
}

/** Hours at 2dp with the trailing zeros trimmed: 4 → "4", 3.95 → "3.95", 8.05 → "8.05". */
function fmtHoursNum(h: number): string {
  if (!Number.isFinite(h)) return "0";
  return String(Math.round(h * 100) / 100);
}

/** Tolerance for the 8-hour comparisons — the inputs are 2dp, the sums are binary floats. */
const HOUR_EPS = 0.0001;
const MAX_DAY_HOURS = 8;

function actionLabel(action: TimeOffApprovalAction): string {
  switch (action) {
    case "APPROVED":              return "Approved";
    case "DENIED":                return "Denied";
    case "DENIED_AFTER_APPROVAL": return "Denied after approval";
    case "HOURS_MODIFIED":        return "Hours modified";
    case "SUBMITTED_FOR_EMPLOYEE": return "Submitted on behalf";
    default:                      return action;
  }
}

/** Timeline dot colour. Anything that takes the request away reads as a denial. */
function actionDotClass(action: TimeOffApprovalAction): string {
  if (action === "APPROVED") return "approved";
  if (action === "HOURS_MODIFIED") return "modified";
  if (action === "SUBMITTED_FOR_EMPLOYEE") return "submitted";
  return "denied";
}

/** Worked hours for a day, defaulting the field an older API build doesn't send. */
function workedHoursOf(d: TimeOffRequestDetailItem): number {
  return d.workedHours ?? 0;
}

/** The ceiling the API enforces for a day: its own `maxHours`, else 8 − worked. */
function maxHoursOf(d: TimeOffRequestDetailItem): number {
  if (d.maxHours != null) return d.maxHours;
  return Math.max(0, Math.round((MAX_DAY_HOURS - workedHoursOf(d)) * 100) / 100);
}

function fmtDateTime(iso: string): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString();
}

function relativeTime(iso: string): string {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "1 day ago";
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks === 1) return "1 week ago";
  if (weeks < 5) return `${weeks} weeks ago`;
  const months = Math.floor(days / 30);
  if (months === 1) return "1 month ago";
  return `${months} months ago`;
}

function statusLabel(status: TimeOffRequestStatus): string {
  switch (status) {
    case "PENDING":   return "Pending";
    case "APPROVED":  return "Approved";
    case "DENIED":    return "Denied";
    case "CANCELLED": return "Cancelled";
    default:          return status;
  }
}

function categoryColorClass(name: string): string {
  const n = (name ?? "").toLowerCase();
  if (n.includes("sick"))                    return "c-sick";
  if (n.includes("vacation"))               return "c-vacation";
  if (n.includes("pto") || n.includes("personal")) return "c-pto";
  return "c-default";
}

// ─── Shared Components ────────────────────────────────────────────────────────

const CategoryChip: React.FC<{ categoryName: string }> = ({ categoryName }) => (
  <span className={`toa-chip toa-chip-${categoryColorClass(categoryName)}`}>
    <span className="toa-chip-dot" />
    {categoryName}
  </span>
);

const StatusPill: React.FC<{ status: TimeOffRequestStatus }> = ({ status }) => (
  <span className={`toa-pill toa-pill-${status.toLowerCase()}`}>
    <span className="toa-pill-dot" />
    {statusLabel(status)}
  </span>
);

/** Marks an Unpaid Time request (TAS-586) beside its category chip. */
const UnpaidTag: React.FC = () => (
  <span className="toa-unpaid-tag" title="Unpaid time — no balance, never paid">Unpaid</span>
);

/**
 * Second line under the employee name: their home supervisor, plus the `#ID` that only
 * shows once the queue is too narrow for the ID column (CSS container query hides that column).
 */
const EmployeeSubLine: React.FC<{ supervisorName?: string | null; altEmployeeID?: string | null }> = ({
  supervisorName, altEmployeeID,
}) => {
  if (!supervisorName && !altEmployeeID) return null;
  return (
    <div className="toa-emp-sub" title={supervisorName ? `Supervisor: ${supervisorName}` : undefined}>
      {supervisorName && <span>Super: {supervisorName}</span>}
      {altEmployeeID && (
        <span className="toa-emp-subid">{supervisorName ? " · " : ""}#{altEmployeeID}</span>
      )}
    </div>
  );
};

/**
 * What to tell the admin when an approval fails: the API's own reason on a 4xx, else the
 * generic line, as before.
 */
const approveFailureMessage = refusalMessageOf;

// ─── Paid-time override confirm (Unpaid Time, TAS-586) ───────────────────────

interface PaidOverrideModalProps {
  /** "approve" — the 422 from an approval; "submit" — the 409 from an on-behalf create. */
  mode: "approve" | "submit";
  employeeName: string;
  /** The PAID_NOT_EXHAUSTED error: its message plus the blocking list. */
  error: unknown;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

/**
 * Unpaid Time may require the employee's paid time to be used up first. When it isn't, the API
 * refuses and names the paid categories still holding time; an admin may go ahead anyway, and
 * the request records who did. Always one request at a time — never offered in bulk.
 */
const PaidOverrideModal: React.FC<PaidOverrideModalProps> = ({
  mode, employeeName, error, onConfirm, onClose,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const blocking = blockingOf(error);
  const message = error instanceof Error && error.message
    ? error.message
    : "This employee still has paid time off available.";

  const handleConfirm = async () => {
    setSubmitting(true);
    try { await onConfirm(); } finally { setSubmitting(false); }
  };

  return (
    <div className="toa-modal-bg" onClick={onClose}>
      <div className="toa-modal" onClick={(e) => e.stopPropagation()}>
        <div className="toa-modal-head toa-modal-head-modify">
          <div className="toa-modal-icon"><AlertTriangle size={22} /></div>
          <div>
            <h3 className="toa-modal-title">Paid time isn’t used up</h3>
            <p className="toa-modal-sub">
              {firstName(employeeName)} still has paid time off, and this policy asks for it to be
              used before unpaid time.
            </p>
          </div>
        </div>
        <div className="toa-modal-body">
          <div className="toa-modal-warn">
            <AlertTriangle size={16} />
            <div>
              <div className="toa-modal-warn-line">{message}</div>
              {blocking.length > 0 && (
                <ul className="toa-gate-list">
                  {blocking.map((b) => (
                    <li key={b.categoryID}>{describeBlocking(b)}</li>
                  ))}
                </ul>
              )}
              <div className="toa-modal-warn-sub">
                {mode === "approve"
                  ? "You can approve it anyway. Your name is recorded on the request as overriding the rule."
                  : "You can submit it anyway. Your name is recorded on the request as overriding the rule."}
              </div>
            </div>
          </div>
        </div>
        <div className="toa-modal-foot">
          <button className="toa-btn toa-btn-ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="toa-btn toa-btn-approve-solid"
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? "Processing…" : mode === "approve" ? "Approve anyway" : "Submit anyway"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Action Modal ─────────────────────────────────────────────────────────────

type ActionModalAction = "approve" | "deny" | "denyApproved";

/** A reason is only meaningful once it says something — matches the API's own blank check. */
const MIN_REASON_CHARS = 3;

interface ActionModalProps {
  action: ActionModalAction;
  request: TimeOffRequestSummary;
  initialNote?: string;
  /**
   * Loaded detail for this request, when the caller has it. Only used to warn on approve
   * that a requested day already has worked time — the modal works fine without it.
   */
  detail?: TimeOffRequestDetail | null;
  onConfirm: (notes: string) => Promise<void>;
  onClose: () => void;
}

const ActionModal: React.FC<ActionModalProps> = ({
  action, request, initialNote = "", detail, onConfirm, onClose,
}) => {
  const [notes, setNotes] = useState(initialNote);
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try { await onConfirm(notes); } finally { setSubmitting(false); }
  };

  const isApprove      = action === "approve";
  const isDenyApproved = action === "denyApproved";

  // Deny-after-approval reverses a decision the employee has already been told about,
  // so the reason is mandatory here where the plain note is optional.
  const reasonOk    = notes.trim().length >= MIN_REASON_CHARS;
  const confirmable = !submitting && (!isDenyApproved || reasonOk);

  // Days the API flagged: already worked enough that approving tips the day past 8 h.
  const overEightDays = isApprove && detail?.dayOverEightWarning
    ? (detail.details ?? []).filter(
        (d) => workedHoursOf(d) + d.hours > MAX_DAY_HOURS + HOUR_EPS
      )
    : [];

  const title =
    isApprove      ? "Approve time off?" :
    isDenyApproved ? "Deny an approved request?" :
                     "Deny time off?";

  const sub =
    isApprove ? "The employee will be notified it was approved."
    : isDenyApproved
      ? isUnpaid(request)
        ? "This request was already approved. Denying it notifies the employee. It’s unpaid time, so no hours are returned to a balance."
        : `This request was already approved. Denying it returns ${fmtHoursNum(request.totalHours)} hrs to the employee's ${request.categoryName} balance and notifies them.`
      : "The employee will be notified it was denied.";

  const confirmLabel =
    isApprove ? "Approve" : isDenyApproved ? "Deny request" : "Deny";

  return (
    <div className="toa-modal-bg" onClick={onClose}>
      <div className="toa-modal" onClick={(e) => e.stopPropagation()}>
        <div className={`toa-modal-head ${isApprove ? "toa-modal-head-approve" : "toa-modal-head-deny"}`}>
          <div className="toa-modal-icon">
            {isApprove ? <CheckCircle size={22} /> : <XCircle size={22} />}
          </div>
          <div>
            <h3 className="toa-modal-title">{title}</h3>
            <p className="toa-modal-sub">{sub}</p>
          </div>
        </div>
        <div className="toa-modal-body">
          <div className="toa-modal-reqline">
            <div className="toa-av">{getInitials(request.employeeName)}</div>
            <div>
              <div className="toa-modal-reqname">{request.employeeName}</div>
              <div className="toa-modal-reqmeta">
                {request.altEmployeeID ? `#${request.altEmployeeID} · ` : ""}
                {request.categoryName}
                {request.startDate
                  ? ` · ${fmtDate(request.startDate)} – ${fmtDate(request.endDate)}`
                  : ""}
                {request.totalHours != null ? ` · ${request.totalHours}h` : ""}
              </div>
              {request.supervisorName && (
                <div className="toa-modal-reqmeta">Super: {request.supervisorName}</div>
              )}
            </div>
          </div>

          {overEightDays.length > 0 && (
            <div className="toa-modal-warn">
              <AlertTriangle size={16} />
              <div>
                {overEightDays.map((d) => (
                  <div className="toa-modal-warn-line" key={d.requestDate}>
                    {fmtShortDate(d.requestDate)} already has {fmtHoursNum(workedHoursOf(d))} hrs
                    {" "}worked; approving {fmtHoursNum(d.hours)} hrs makes the day{" "}
                    {fmtHoursNum(workedHoursOf(d) + d.hours)} hrs.
                  </div>
                ))}
                <div className="toa-modal-warn-sub">
                  You can still approve — payroll can correct the hours afterwards.
                </div>
              </div>
            </div>
          )}

          <label className="toa-modal-label">
            {isDenyApproved ? "Reason " : "Decision note "}
            <span className="toa-modal-opt">
              {isDenyApproved
                ? "— required, shared with the employee"
                : "— optional, shared with the employee"}
            </span>
          </label>
          <textarea
            className="toa-modal-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={
              isDenyApproved
                ? "Why is this approved request being denied?"
                : "Add context for this decision…"
            }
            rows={3}
          />
          {isDenyApproved && !reasonOk && (
            <div className="toa-modal-hint">
              Enter a reason before denying — the employee sees it.
            </div>
          )}
        </div>
        <div className="toa-modal-foot">
          <button className="toa-btn toa-btn-ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className={`toa-btn ${isApprove ? "toa-btn-approve-solid" : "toa-btn-deny-solid"}`}
            onClick={handleConfirm}
            disabled={!confirmable}
          >
            {submitting ? "Processing…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Request Row ─────────────────────────────────────────────────────────────

interface RequestRowProps {
  req: TimeOffRequestSummary;
  /** Show the bulk-select column (the page decides; a row the caller can't act on stays blank). */
  showBulk: boolean;
  expanded: boolean;
  detail: TimeOffRequestDetail | null;
  loadingDetail: boolean;
  selected: boolean;
  removing: boolean;
  detailNote: string;
  /** Per-category balances for this row's employee; null until loaded. */
  balances: EmployeeTimeOffBalanceRow[] | null;
  loadingBalances: boolean;
  balancesError: boolean;
  onToggleExpand: () => void;
  onToggleSelect: () => void;
  onApprove: (req: TimeOffRequestSummary, note?: string) => void;
  onDeny: (req: TimeOffRequestSummary, note?: string) => void;
  /**
   * Does this viewer hold `editApprovedTimeOffRequests`? The per-request `canEditApproved`
   * is ANDed with it, so an older API that omits the flag still respects the role.
   */
  allowEditApproved: boolean;
  /** Correcting an already-approved request — payroll only, expanded rows only. */
  onModifyHours: (req: TimeOffRequestSummary) => void;
  onDenyApproved: (req: TimeOffRequestSummary) => void;
  onDetailNoteChange: (note: string) => void;
  onRetryBalances: () => void;
}

// ─── Time off balances band (expanded request) ───────────────────────────────

interface BalancesBandProps {
  req: TimeOffRequestSummary;
  rows: EmployeeTimeOffBalanceRow[] | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

/**
 * Answers "does this person actually have the time?" without leaving the queue.
 *
 * Available is **net** — the API debits the balance when a request is submitted, so this
 * request's hours are already held out of the figure shown. That's why the callout reports the
 * post-approval balance instead of subtracting the request again, and why Pending (which
 * includes this request) is never taken off Available a second time.
 */
const BalancesBand: React.FC<BalancesBandProps> = ({ req, rows, loading, error, onRetry }) => {
  const header = (
    <div className="toa-dh">
      <Wallet size={15} />
      Time off balances
    </div>
  );

  if (loading) {
    return (
      <div className="toa-bal-band">
        {header}
        <div className="toa-detail-loading">Loading balances…</div>
      </div>
    );
  }

  if (error || !rows) {
    return (
      <div className="toa-bal-band">
        {header}
        <div className="toa-bal-err">
          <AlertTriangle size={15} />
          Couldn’t load balances.
          <button type="button" className="toa-bal-retry" onClick={onRetry}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // An employee with no policy assigned gets no rows at all from the API.
  if (rows.length === 0) {
    return (
      <div className="toa-bal-band">
        {header}
        <div className="toa-bal-nopolicy">
          <AlertTriangle size={19} />
          <div>
            <div className="toa-bal-nopolicy-t">No time off policy assigned</div>
            <div className="toa-bal-nopolicy-s">
              {firstName(req.employeeName)} can’t accrue or draw down a balance until a policy is
              assigned on the Employees page. Approving this request won’t deduct from any bucket.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const cat = rows.find((r) => r.timeOffCategoryID === req.timeOffCategoryID);
  // Unpaid Time (TAS-586) has no balance — the API sends 0 on its row, so never do the maths.
  const unpaid = isUnpaid(req) || isUnpaid(cat);
  const shortfall = !unpaid && cat != null && cat.currentBalanceHours < 0;
  const gateBlocking = unpaid && cat?.isRequestable === false ? cat.blocking ?? [] : [];

  return (
    <div className="toa-bal-band">
      {header}
      <div className="toa-bal-split">
        <div className="toa-bal-tblwrap">
          <TimeOffBalancesTable
            rows={rows}
            highlightCategoryID={req.timeOffCategoryID}
            showReadOnlyNote={false}
            showRequestability
          />
        </div>

        {unpaid ? (
          <div className={`toa-bal-callout${cat?.isRequestable === false ? " short" : ""}`}>
            <div className="toa-bal-eq">
              <b>Unpaid time</b> — no balance. Nothing is deducted, paid or exported.
            </div>
            {cat?.isRequestable === false ? (
              <div className="toa-bal-note short">
                <AlertTriangle size={15} />
                <div>
                  {notRequestableText(cat)}.
                  {gateBlocking.length > 0 && (
                    <ul className="toa-gate-list">
                      {gateBlocking.map((b) => (
                        <li key={b.categoryID}>{describeBlocking(b)}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ) : (
              <div className="toa-bal-note">
                <CheckCircle size={15} />
                Full 8-hour days only.
              </div>
            )}
          </div>
        ) : cat ? (
          <div className={`toa-bal-callout${shortfall ? " short" : ""}`}>
            <div className="toa-bal-eq">
              This request’s <b>{req.totalHours}h</b> is already held out of Available.
            </div>
            <div className={`toa-bal-result${shortfall ? " short" : ""}`}>
              {fmtHrs(cat.currentBalanceHours)}
            </div>
            <div className="toa-bal-reslab">
              {shortfall ? "over available balance" : "balance after approval"}
            </div>
            {shortfall ? (
              <div className="toa-bal-note short">
                <AlertTriangle size={15} />
                {firstName(req.employeeName)} is negative on {cat.categoryName} by{" "}
                {fmtHrs(Math.abs(cat.currentBalanceHours))}.
              </div>
            ) : (
              <div className="toa-bal-note">
                <CheckCircle size={15} />
                Covered by {cat.categoryName} balance.
              </div>
            )}
          </div>
        ) : (
          <div className="toa-bal-callout short">
            <div className="toa-bal-note short">
              <AlertTriangle size={15} />
              {req.categoryName} isn’t on {firstName(req.employeeName)}’s policy, so this request
              won’t draw down a balance.
            </div>
          </div>
        )}
      </div>
      <div className="toa-bal-foot">
        <Clock size={14} />
        Balances are as of today and read-only here. Accruals and adjustments are managed in payroll.
      </div>
    </div>
  );
};

// ─── Modify Hours Modal ───────────────────────────────────────────────────────

interface ModifyHoursModalProps {
  request: TimeOffRequestSummary;
  detail: TimeOffRequestDetail;
  /** Same rows the expanded row shows, so the band and the "balance after" line agree. */
  balances: EmployeeTimeOffBalanceRow[] | null;
  loadingBalances: boolean;
  balancesError: boolean;
  onRetryBalances: () => void;
  onSaved: (totalHours: number) => void | Promise<void>;
  onClose: () => void;
}

/**
 * Payroll's correction surface for an already-approved request: per-day hours on the dates the
 * request already has, nothing else. Dates and category are not editable here by design.
 *
 * Three ceilings are enforced client-side so the common mistakes never reach the API — worked +
 * time off over 8 h on a day, a change that would overdraw the category, and zeroing every day
 * (which is a denial, not a modification). The API re-checks all three; a 422 lands inline.
 */
const ModifyHoursModal: React.FC<ModifyHoursModalProps> = ({
  request, detail, balances, loadingBalances, balancesError, onRetryBalances, onSaved, onClose,
}) => {
  const days = detail.details ?? [];

  const [raw, setRaw] = useState<Record<string, string>>(() => {
    const seed: Record<string, string> = {};
    days.forEach((d) => { seed[d.requestDate] = String(d.hours); });
    return seed;
  });
  const [reason, setReason]         = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError]     = useState<string | null>(null);
  /** Day the API named in a DAY_OVER_EIGHT refusal, highlighted in the table. */
  const [errorDate, setErrorDate]   = useState<string | null>(null);

  const valueOf = (dateKey: string): number => {
    const t = (raw[dateKey] ?? "").trim();
    if (t === "") return 0;
    const n = Number(t);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  };

  const hasInvalid = days.some((d) => {
    const t = (raw[d.requestDate] ?? "").trim();
    if (t === "") return false;
    const n = Number(t);
    return !Number.isFinite(n) || n < 0;
  });

  const oldTotal = days.reduce((s, d) => s + d.hours, 0);
  const newTotal = days.reduce((s, d) => s + valueOf(d.requestDate), 0);
  const delta    = newTotal - oldTotal;

  // Available is net — this request's current hours are already held out of it — so a change of
  // `delta` moves the balance by exactly −delta.
  const balanceRow = balances?.find((b) => b.timeOffCategoryID === request.timeOffCategoryID);

  // Unpaid Time (TAS-586): full days only, so each day is kept (8) or dropped (0). There is no
  // balance to overdraw, and the API skips the worked + time off > 8 h check for it too.
  const unpaid = isUnpaid(request) || isUnpaid(detail) || isUnpaid(balanceRow);

  const overDays = unpaid ? [] : days.filter(
    (d) => workedHoursOf(d) + valueOf(d.requestDate) > MAX_DAY_HOURS + HOUR_EPS
  );

  const legacyRow  = detail.employeeBalances?.find((b) => b.categoryName === request.categoryName);
  const available  = unpaid ? null
                   : balanceRow ? balanceRow.currentBalanceHours
                   : legacyRow  ? legacyRow.balance
                   : null;
  const balanceAfter   = available == null ? null : available - delta;
  const balanceShort   = balanceAfter != null && balanceAfter < -HOUR_EPS;

  const allZero   = newTotal <= HOUR_EPS;
  const reasonOk  = reason.trim().length >= MIN_REASON_CHARS;
  const canSave   = !submitting && !hasInvalid && overDays.length === 0
                    && !balanceShort && !allZero && reasonOk;

  const handleSave = async () => {
    setSubmitting(true);
    setApiError(null);
    setErrorDate(null);
    try {
      const result = await API.modifyTimeOffRequestHours(
        request.timeOffRequestID,
        reason.trim(),
        days.map((d) => ({ requestDate: d.requestDate, hours: valueOf(d.requestDate) })),
      );
      await onSaved(result?.totalHours ?? newTotal);
    } catch (err) {
      console.error("Modify hours failed:", err);
      if (err instanceof API.TimeOffActionError) {
        setApiError(err.message);
        if ((err.code === "DAY_OVER_EIGHT" || err.code === "FULL_DAYS_ONLY") && err.requestDate) {
          setErrorDate(err.requestDate.slice(0, 10));
        }
      } else {
        setApiError("Failed to change the hours on this request. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="toa-modal-bg" onClick={onClose}>
      <div className="toa-modal toa-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="toa-modal-head toa-modal-head-modify">
          <div className="toa-modal-icon"><Pencil size={22} /></div>
          <div>
            <h3 className="toa-modal-title">Modify approved hours</h3>
            <p className="toa-modal-sub">
              {unpaid
                ? "Unpaid time is full days only — keep a day at 8 hrs or drop it to 0. The employee is notified and the change is recorded in the approval history."
                : "Change the hours on the days this request already covers. The employee is notified and the change is recorded in the approval history."}
            </p>
          </div>
        </div>

        <div className="toa-modal-body toa-modal-body-scroll">
          <div className="toa-modal-reqline">
            <div className="toa-av">{getInitials(request.employeeName)}</div>
            <div>
              <div className="toa-modal-reqname">{request.employeeName}</div>
              <div className="toa-modal-reqmeta">
                {request.altEmployeeID ? `#${request.altEmployeeID} · ` : ""}
                {request.categoryName}
                {request.startDate
                  ? ` · ${fmtDate(request.startDate)} – ${fmtDate(request.endDate)}`
                  : ""}
              </div>
            </div>
          </div>

          <BalancesBand
            req={request}
            rows={balances}
            loading={loadingBalances}
            error={balancesError}
            onRetry={onRetryBalances}
          />

          <table className="toa-mini-table toa-modify-table">
            <thead>
              <tr>
                <th>Date</th>
                <th className="toa-num">Worked</th>
                <th className="toa-num">Time off</th>
                <th className="toa-num">Day total</th>
              </tr>
            </thead>
            <tbody>
              {days.map((d) => {
                const worked = workedHoursOf(d);
                const hrs    = valueOf(d.requestDate);
                const total  = worked + hrs;
                const over   = !unpaid && total > MAX_DAY_HOURS + HOUR_EPS;
                const flagged = errorDate === d.requestDate.slice(0, 10);
                return (
                  <tr key={d.requestDate} className={flagged ? "toa-row-flagged" : undefined}>
                    <td>{fmtDate(d.requestDate)}</td>
                    <td className="toa-num">{fmtHoursNum(worked)}</td>
                    <td className="toa-num">
                      {unpaid ? (
                        <select
                          className={`toa-hin${flagged ? " over" : ""}`}
                          value={hrs > HOUR_EPS ? String(UNPAID_DAY_HOURS) : "0"}
                          disabled={submitting}
                          onChange={(e) =>
                            setRaw((prev) => ({ ...prev, [d.requestDate]: e.target.value }))
                          }
                        >
                          <option value={String(UNPAID_DAY_HOURS)}>{UNPAID_DAY_HOURS} (full day)</option>
                          <option value="0">0 (drop day)</option>
                        </select>
                      ) : (
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className={`toa-hin${over ? " over" : ""}`}
                          value={raw[d.requestDate] ?? ""}
                          disabled={submitting}
                          onChange={(e) =>
                            setRaw((prev) => ({ ...prev, [d.requestDate]: e.target.value }))
                          }
                        />
                      )}
                    </td>
                    <td className={`toa-num toa-daytotal${over ? " over" : ""}`}>
                      {fmtHoursNum(total)}
                      {over && <span className="toa-maxhint">max {fmtHoursNum(maxHoursOf(d))}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="toa-modify-summary">
            <div className="toa-ms-line">
              Total <b>{fmtHoursNum(oldTotal)}</b> → <b>{fmtHoursNum(newTotal)}</b> hrs
            </div>
            {unpaid ? (
              <div className="toa-ms-line">Unpaid · no balance</div>
            ) : (
              <div className={`toa-ms-line${balanceShort ? " short" : ""}`}>
                Balance after:{" "}
                <b>{balanceAfter == null ? "—" : `${fmtHoursNum(balanceAfter)} hrs`}</b>
              </div>
            )}
          </div>

          {allZero && (
            <div className="toa-modal-hint">
              All days are 0 — use Deny approved instead.
            </div>
          )}
          {overDays.length > 0 && (
            <div className="toa-modal-err">
              <AlertTriangle size={15} />
              {overDays.length === 1
                ? `${fmtDate(overDays[0].requestDate)} goes over 8 hrs — max ${fmtHoursNum(maxHoursOf(overDays[0]))} hrs of time off that day.`
                : `${overDays.length} days go over 8 hrs. Lower them to the max shown.`}
            </div>
          )}
          {balanceShort && (
            <div className="toa-modal-err">
              <AlertTriangle size={15} />
              {firstName(request.employeeName)} doesn’t have the balance for this increase.
            </div>
          )}

          <label className="toa-modal-label">
            Reason{" "}
            <span className="toa-modal-opt">— required, shared with the employee</span>
          </label>
          <textarea
            className="toa-modal-textarea"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why are these hours changing?"
            rows={3}
            disabled={submitting}
          />
          {!reasonOk && (
            <div className="toa-modal-hint">
              Enter a reason before saving — the employee sees it.
            </div>
          )}

          {apiError && (
            <div className="toa-modal-err">
              <AlertTriangle size={15} />
              {apiError}
            </div>
          )}
        </div>

        <div className="toa-modal-foot">
          <button className="toa-btn toa-btn-ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="toa-btn toa-btn-approve-solid"
            onClick={handleSave}
            disabled={!canSave}
          >
            {submitting ? "Saving…" : "Save hours"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── New Request Modal (submitted on an employee's behalf) ────────────────────

/** Per-day hours: half-hour steps, at least half an hour, never more than a full day. */
const MIN_DAY_HOURS = 0.5;
const DAY_STEP = 0.5;
const DEFAULT_DAY_HOURS = 8;
/**
 * Tenant flag (2020PH / 2020NV): weekends and the employee's holidays appear in the hours table
 * as optional rows defaulting to 0 hrs; only days with hours > 0 are submitted. Off everywhere
 * else, where the range is weekdays only.
 */
const ALLOW_WEEKEND_HOLIDAY_DAYS = config.allowWeekendHolidayTimeOff === true;
/** A request spanning more than a quarter is a typo'd year, not a request. Bounds the table. */
const MAX_RANGE_DAYS = 92;

const WEEKDAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Local-safe "YYYY-MM-DD". `toISOString()` would shift the day for anyone east of UTC. */
function toDateKey(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function parseDateKey(dateKey: string): Date | null {
  if (!dateKey) return null;
  const d = new Date(`${dateKey.slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isWeekend(dateKey: string): boolean {
  const d = parseDateKey(dateKey);
  if (!d) return false;
  const dow = d.getDay();
  return dow === 0 || dow === 6;
}

/** Day-of-week label for a requested day, e.g. "Mon". */
function weekdayLabel(dateKey: string): string {
  const d = parseDateKey(dateKey);
  return d ? WEEKDAY_ABBR[d.getDay()] : "";
}

/** Calendar days in [start, end] inclusive, 0 when the range is empty or backwards. */
function rangeSpanDays(startDate: string, endDate: string): number {
  const a = parseDateKey(startDate);
  const b = parseDateKey(endDate);
  if (!a || !b || b < a) return 0;
  return Math.round((b.getTime() - a.getTime()) / 86400000) + 1;
}

/**
 * The requestable days in [start, end] inclusive: weekdays only, or every calendar day when
 * `includeWeekends` (the weekend/holiday tenant flag). An over-long range returns nothing rather
 * than a table of hundreds of rows; the modal names that case separately.
 */
function expandRequestDays(startDate: string, endDate: string, includeWeekends: boolean): string[] {
  const span = rangeSpanDays(startDate, endDate);
  if (span === 0 || span > MAX_RANGE_DAYS) return [];
  const cur = parseDateKey(startDate) as Date;
  const out: string[] = [];
  for (let i = 0; i < span; i++) {
    const dow = cur.getDay();
    if (includeWeekends || (dow !== 0 && dow !== 6)) out.push(toDateKey(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/** Most employees to render in the typeahead at once; the rest wait for a narrower query. */
const TYPEAHEAD_LIMIT = 50;

interface NewTimeOffRequestModalProps {
  /**
   * Called after a successful submit — the page closes the modal, reloads and toasts.
   * `approveError` is set when "Approve now" was asked for but the approval failed (the
   * request still exists, pending).
   */
  onSubmitted: (employeeName: string, approved: boolean, approveError?: string) => void | Promise<void>;
  onClose: () => void;
}

/**
 * Payroll / HR raising a request the employee never submitted — a paper slip, a phone call, a
 * correction after the fact. One modal, four steps: who, which bucket, which days, how long.
 *
 * Everything here is a pre-flight of the server's own rules (weekdays, half-hour steps, ≤ 8 h a
 * day, the category balance), except the two the client cannot judge: tenure eligibility is
 * evaluated at the REQUESTED date server-side, so an "Eligible on …" category stays selectable;
 * and the balance is a warning, not a block, because the API is the authority on annual caps and
 * on what a past-dated day draws from. A refusal comes back as a 400/403 whose message is shown
 * verbatim — it is the only thing that tells the admin what to change.
 */
const NewTimeOffRequestModal: React.FC<NewTimeOffRequestModalProps> = ({ onSubmitted, onClose }) => {
  const [employees, setEmployees]             = useState<API.EligibleTimeOffEmployee[] | null>(null);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [employeesError, setEmployeesError]   = useState(false);

  const [query, setQuery]     = useState("");
  const [listOpen, setListOpen] = useState(true);
  const [picked, setPicked]   = useState<API.EligibleTimeOffEmployee | null>(null);

  const [balances, setBalances]               = useState<EmployeeTimeOffBalanceRow[] | null>(null);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [balancesError, setBalancesError]     = useState(false);

  const [categoryID, setCategoryID] = useState<number | null>(null);
  const [startDate, setStartDate]   = useState("");
  const [endDate, setEndDate]       = useState("");
  const [hours, setHours]           = useState<Record<string, string>>({});
  const [notes, setNotes]           = useState("");
  const [approveNow, setApproveNow] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError]     = useState<string | null>(null);
  /** Unpaid Time's 409 PAID_NOT_EXHAUSTED, held while the admin decides whether to override. */
  const [paidGate, setPaidGate]     = useState<API.TimeOffActionError | null>(null);

  const loadEmployees = useCallback(async () => {
    setLoadingEmployees(true);
    setEmployeesError(false);
    try {
      const rows = await API.getEligibleTimeOffEmployees();
      setEmployees(rows);
    } catch (err) {
      console.error("Failed to load eligible employees:", err);
      setEmployeesError(true);
      setEmployees(null);
    } finally {
      setLoadingEmployees(false);
    }
  }, []);

  useEffect(() => { loadEmployees(); }, [loadEmployees]);

  const loadBalancesFor = useCallback(async (employeeID: number) => {
    setLoadingBalances(true);
    setBalancesError(false);
    setBalances(null);
    try {
      const rows = await getEmployeeTimeOffBalance(employeeID);
      setBalances(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error("Failed to load time off balances:", err);
      setBalancesError(true);
    } finally {
      setLoadingBalances(false);
    }
  }, []);

  useEffect(() => {
    if (!picked) { setBalances(null); setBalancesError(false); return; }
    loadBalancesFor(picked.employeeID);
  }, [picked, loadBalancesFor]);

  const categoryRows  = balances ?? [];
  const selectedRow   = categoryRows.find((r) => r.timeOffCategoryID === categoryID) ?? null;
  /**
   * Unpaid Time (TAS-586): no balance, full 8-hour weekdays only, never on a holiday — whatever
   * the weekend/holiday tenant flag says. The API enforces all of it; this just offers only that.
   */
  const unpaidSel     = isUnpaid(selectedRow);
  const allowOptionalDays = ALLOW_WEEKEND_HOLIDAY_DAYS && !unpaidSel;

  const dayKeys = useMemo(
    () => expandRequestDays(startDate, endDate, allowOptionalDays),
    [startDate, endDate, allowOptionalDays]
  );

  /** The employee's holidays in the range, date key → name. Only fetched under the flag. */
  const [holidayNames, setHolidayNames] = useState<Map<string, string>>(() => new Map());
  /** Days whose hours the admin typed — a late-arriving holiday must not overwrite those. */
  const touchedDaysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!ALLOW_WEEKEND_HOLIDAY_DAYS) return;
    const span = rangeSpanDays(startDate, endDate);
    if (!picked || span === 0 || span > MAX_RANGE_DAYS) {
      setHolidayNames(new Map());
      return;
    }
    let cancelled = false;
    getEmployeeHolidays(picked.employeeID, startDate, endDate).then((rows) => {
      if (cancelled) return;
      setHolidayNames(new Map(rows.map((r) => [String(r.holidayDate).slice(0, 10), r.name])));
    });
    return () => { cancelled = true; };
  }, [picked, startDate, endDate]);

  /** Weekend and holiday rows are optional under the flag: 0 hrs allowed, 0 hrs by default. */
  const isOptionalDay = useCallback(
    (dateKey: string): boolean =>
      ALLOW_WEEKEND_HOLIDAY_DAYS && (isWeekend(dateKey) || holidayNames.has(dateKey)),
    [holidayNames]
  );

  // Rebuild the hours table when the range moves, keeping whatever the admin already typed on
  // the days that survived the change. Under the flag an untouched day re-takes its default,
  // so a holiday that loads after the range was picked drops to 0.
  useEffect(() => {
    setHours((prev) => {
      const next: Record<string, string> = {};
      dayKeys.forEach((k) => {
        const keep = prev[k] !== undefined
          && (!ALLOW_WEEKEND_HOLIDAY_DAYS || touchedDaysRef.current.has(k));
        next[k] = keep ? prev[k] : (isOptionalDay(k) ? "0" : String(DEFAULT_DAY_HOURS));
      });
      return next;
    });
  }, [dayKeys, isOptionalDay]);

  // ── Employee typeahead (client-side; the list is the admin's scoped roster) ──
  const q = query.trim().toLowerCase().replace(/^#/, "");
  const matches = (employees ?? []).filter((e) => {
    if (!q) return true;
    return e.name.toLowerCase().includes(q)
      || (e.altEmployeeID ?? "").toLowerCase().includes(q);
  });
  const shown = matches.slice(0, TYPEAHEAD_LIMIT);

  const pickEmployee = (e: API.EligibleTimeOffEmployee) => {
    setPicked(e);
    setListOpen(false);
    setQuery("");
    setCategoryID(null);
    setApproveNow(false);
    setApiError(null);
  };

  const clearEmployee = () => {
    setPicked(null);
    setCategoryID(null);
    setApproveNow(false);
    setApiError(null);
    setListOpen(true);
  };

  // ── Validation ──────────────────────────────────────────────────────────────
  const startIsWeekend = !allowOptionalDays && startDate !== "" && isWeekend(startDate);
  const endIsWeekend   = !allowOptionalDays && endDate !== "" && isWeekend(endDate);
  const rangeBackwards = startDate !== "" && endDate !== "" && endDate < startDate;
  const rangeTooLong   = rangeSpanDays(startDate, endDate) > MAX_RANGE_DAYS;
  const noWeekdays     = !allowOptionalDays && startDate !== "" && endDate !== ""
                         && !rangeBackwards && !rangeTooLong && dayKeys.length === 0;

  /** Unpaid days are always a full day — the typed hours (kept for a switch back) are ignored. */
  const hourValueOf = (dateKey: string): number => {
    if (unpaidSel) return UNPAID_DAY_HOURS;
    const t = (hours[dateKey] ?? "").trim();
    if (t === "") return NaN;
    const n = Number(t);
    return Number.isFinite(n) ? n : NaN;
  };
  /** A holiday we know about in the range. Unpaid time is never requested on one. */
  const isKnownHoliday = (dateKey: string): boolean => holidayNames.has(dateKey);

  /**
   * A day is bad when it is not a number, out of the 0.5–8 band, or off the half-hour step.
   * An optional (weekend/holiday) day may also be exactly 0 — it is simply not submitted.
   */
  const dayIsInvalid = (dateKey: string): boolean => {
    if (unpaidSel) return false;
    const n = hourValueOf(dateKey);
    if (!Number.isFinite(n)) return true;
    if (isOptionalDay(dateKey) && Math.abs(n) <= HOUR_EPS) return false;
    if (n < MIN_DAY_HOURS - HOUR_EPS || n > MAX_DAY_HOURS + HOUR_EPS) return true;
    return Math.abs(n / DAY_STEP - Math.round(n / DAY_STEP)) > HOUR_EPS;
  };

  const invalidDays = dayKeys.filter(dayIsInvalid);
  /**
   * The days actually submitted: under the flag, 0-hour rows are dropped client-side; for unpaid
   * time, known holidays are dropped (the API would refuse them).
   */
  const requestedDayKeys = unpaidSel
    ? dayKeys.filter((k) => !isKnownHoliday(k))
    : ALLOW_WEEKEND_HOLIDAY_DAYS
      ? dayKeys.filter((k) => hourValueOf(k) > HOUR_EPS)
      : dayKeys;
  const noHoursRequested = (ALLOW_WEEKEND_HOLIDAY_DAYS || unpaidSel)
    && dayKeys.length > 0 && invalidDays.length === 0 && requestedDayKeys.length === 0;
  const totalHours = requestedDayKeys.reduce((s, k) => {
    const n = hourValueOf(k);
    return s + (Number.isFinite(n) ? n : 0);
  }, 0);

  const noPolicy      = balances != null && balances.length === 0;
  // No balance-after for unpaid time — its row's 0 is a placeholder, not a balance.
  const balanceAfter  = selectedRow && !unpaidSel ? selectedRow.currentBalanceHours - totalHours : null;
  const balanceShort  = balanceAfter != null && balanceAfter < -HOUR_EPS;

  const canSubmit =
    !submitting
    && picked != null
    && categoryID != null
    && startDate !== ""
    && endDate !== ""
    && !rangeBackwards
    && !startIsWeekend
    && !endIsWeekend
    && requestedDayKeys.length > 0
    && invalidDays.length === 0;

  /**
   * `overrideExhausted` is only ever sent from the paid-time confirm, after the API answered
   * 409 PAID_NOT_EXHAUSTED for exactly this request and the admin chose to go ahead.
   */
  const handleSubmit = async (overrideExhausted = false) => {
    if (!picked || categoryID == null) return;
    setSubmitting(true);
    setApiError(null);
    try {
      const result = await API.submitTimeOffRequestForEmployee({
        employeeID: picked.employeeID,
        timeOffCategoryID: categoryID,
        days: requestedDayKeys.map((k) => ({ requestDate: k, hours: hourValueOf(k) })),
        notes: notes.trim() || undefined,
        // Never ask for an approval this admin was not offered — the API 403s before inserting.
        approveNow: approveNow && picked.canApprove ? true : undefined,
        overrideExhausted: overrideExhausted ? true : undefined,
      });
      setPaidGate(null);
      await onSubmitted(picked.name, result?.status === "APPROVED", result?.approveError);
    } catch (err) {
      console.error("Submit on behalf failed:", err);
      if (!overrideExhausted && isPaidNotExhaustedError(err) && err instanceof API.TimeOffActionError) {
        setPaidGate(err);
        return;
      }
      setPaidGate(null);
      setApiError(
        err instanceof API.TimeOffActionError && err.message
          ? err.message
          : "Failed to submit the request. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <div className="toa-modal-bg" onClick={onClose}>
      <div className="toa-modal toa-modal-wide toa-modal-newreq" onClick={(e) => e.stopPropagation()}>
        <div className="toa-modal-head toa-modal-head-new">
          <div className="toa-modal-icon"><Plus size={22} /></div>
          <div>
            <h3 className="toa-modal-title">New time off request</h3>
            <p className="toa-modal-sub">
              Raised on an employee’s behalf. It enters the normal approval queue and the
              employee is notified that you submitted it for them.
            </p>
          </div>
        </div>

        <div className="toa-modal-body toa-modal-body-scroll">

          {/* ── 1. Employee ───────────────────────────────────────────────── */}
          <label className="toa-modal-label">
            Employee <span className="toa-modal-opt">— required</span>
          </label>
          {picked ? (
            <div className="toa-modal-reqline toa-picked">
              <div className="toa-av">{getInitials(picked.name)}</div>
              <div className="toa-picked-txt">
                <div className="toa-modal-reqname">{picked.name}</div>
                <div className="toa-modal-reqmeta">
                  {picked.altEmployeeID ? `#${picked.altEmployeeID}` : "No employee ID"}
                  {picked.canApprove ? " · you can approve for this employee" : ""}
                </div>
              </div>
              <button
                type="button"
                className="toa-ta-clear"
                onClick={clearEmployee}
                disabled={submitting}
                aria-label="Choose a different employee"
              >
                <X size={15} />
              </button>
            </div>
          ) : (
            <div className="toa-typeahead">
              <div className="toa-searchwrap">
                <Search size={17} className="toa-search-icon" />
                <input
                  type="text"
                  className="toa-input"
                  placeholder={loadingEmployees ? "Loading employees…" : "Search by name or employee #…"}
                  value={query}
                  disabled={submitting || loadingEmployees}
                  onChange={(e) => { setQuery(e.target.value); setListOpen(true); }}
                  onFocus={() => setListOpen(true)}
                />
              </div>
              {employeesError ? (
                <div className="toa-bal-err">
                  <AlertTriangle size={15} />
                  Couldn’t load the employee list.
                  <button type="button" className="toa-bal-retry" onClick={loadEmployees}>
                    Retry
                  </button>
                </div>
              ) : listOpen && !loadingEmployees ? (
                <div className="toa-ta-list">
                  {shown.length === 0 ? (
                    <div className="toa-ta-empty">No employees match “{query.trim()}”.</div>
                  ) : (
                    <>
                      {shown.map((e) => (
                        <button
                          type="button"
                          key={e.employeeID}
                          className="toa-ta-opt"
                          onClick={() => pickEmployee(e)}
                        >
                          <span className="toa-ta-name">{e.name}</span>
                          <span className="toa-ta-id">
                            {e.altEmployeeID ? `#${e.altEmployeeID}` : "—"}
                          </span>
                        </button>
                      ))}
                      {matches.length > shown.length && (
                        <div className="toa-ta-more">
                          {matches.length - shown.length} more — keep typing to narrow it down.
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : null}
            </div>
          )}

          {/* ── 2. Category ───────────────────────────────────────────────── */}
          {picked && (
            <div className="toa-newreq-sec">
              <label className="toa-modal-label">
                Category <span className="toa-modal-opt">— required</span>
              </label>
              {loadingBalances ? (
                <div className="toa-detail-loading">Loading balances…</div>
              ) : balancesError ? (
                <div className="toa-bal-err">
                  <AlertTriangle size={15} />
                  Couldn’t load balances.
                  <button
                    type="button"
                    className="toa-bal-retry"
                    onClick={() => loadBalancesFor(picked.employeeID)}
                  >
                    Retry
                  </button>
                </div>
              ) : noPolicy ? (
                <div className="toa-modal-err">
                  <AlertTriangle size={15} />
                  {firstName(picked.name)} has no time off policy assigned, so there is no
                  category to request against. Assign one on the Employees page first.
                </div>
              ) : (
                <div className="toa-cat-grid">
                  {categoryRows.map((row) => {
                    const on = categoryID === row.timeOffCategoryID;
                    const ineligible = row.isEligible === false;
                    return (
                      <button
                        type="button"
                        key={row.timeOffCategoryID}
                        className={`toa-cat-opt${on ? " on" : ""}`}
                        onClick={() => setCategoryID(row.timeOffCategoryID)}
                        disabled={submitting}
                      >
                        <span className="toa-cat-name">{row.categoryName}</span>
                        <span className="toa-cat-bal">
                          {isUnpaid(row) ? "Unpaid · no balance" : `${fmtHrs(row.currentBalanceHours)} available`}
                        </span>
                        {notRequestableText(row) && (
                          <span
                            className="toa-cat-elig"
                            title={(row.blocking ?? []).map(describeBlocking).join("\n") || undefined}
                          >
                            {notRequestableText(row)}
                          </span>
                        )}
                        {ineligible && (
                          <span className="toa-cat-elig">
                            {row.eligibleOn
                              ? `Eligible on ${fmtDate(row.eligibleOn)}`
                              : "Not yet eligible"}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── 3. Dates ──────────────────────────────────────────────────── */}
          {picked && (
            <div className="toa-newreq-sec">
              <label className="toa-modal-label">
                Dates{" "}
                <span className="toa-modal-opt">
                  {unpaidSel
                    ? "— weekdays only, no holidays; past dates are allowed"
                    : ALLOW_WEEKEND_HOLIDAY_DAYS
                      ? "— weekends and holidays are optional (default 0 hrs); past dates are allowed"
                      : "— weekdays only; past dates are allowed"}
                </span>
              </label>
              <div className="toa-date-pair">
                <input
                  type="date"
                  className="toa-input toa-input-date"
                  value={startDate}
                  disabled={submitting}
                  onChange={(e) => {
                    const v = e.target.value;
                    setStartDate(v);
                    // Most on-behalf requests are a single day; carrying the end date along
                    // also stops a backwards range the moment the start moves past it.
                    if (!endDate || endDate < v) setEndDate(v);
                  }}
                />
                <span className="toa-date-sep">→</span>
                <input
                  type="date"
                  className="toa-input toa-input-date"
                  value={endDate}
                  disabled={submitting}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              {(startIsWeekend || endIsWeekend) && (
                <div className="toa-modal-err">
                  <AlertTriangle size={15} />
                  {startIsWeekend && endIsWeekend
                    ? "Both dates fall on a weekend — time off is requested on weekdays only."
                    : `The ${startIsWeekend ? "start" : "end"} date falls on a weekend — time off is requested on weekdays only.`}
                </div>
              )}
              {rangeBackwards && (
                <div className="toa-modal-err">
                  <AlertTriangle size={15} />
                  The end date is before the start date.
                </div>
              )}
              {rangeTooLong && (
                <div className="toa-modal-err">
                  <AlertTriangle size={15} />
                  That range is longer than {MAX_RANGE_DAYS} days — check the dates.
                </div>
              )}
              {noWeekdays && (
                <div className="toa-modal-err">
                  <AlertTriangle size={15} />
                  That range has no weekdays in it.
                </div>
              )}
              {noHoursRequested && (
                <div className="toa-modal-err">
                  <AlertTriangle size={15} />
                  {unpaidSel
                    ? "Every day in that range is a holiday — unpaid time can’t be taken on one."
                    : "Add hours to at least one day."}
                </div>
              )}
            </div>
          )}

          {/* ── 4. Hours per day ──────────────────────────────────────────── */}
          {picked && dayKeys.length > 0 && (
            <div className="toa-newreq-sec">
              <label className="toa-modal-label">
                Hours per day{" "}
                <span className="toa-modal-opt">
                  {unpaidSel
                    ? "— unpaid time is full 8-hour days only"
                    : ALLOW_WEEKEND_HOLIDAY_DAYS
                      ? "— half-hour steps, 0.5 to 8 hrs; weekend/holiday days may stay at 0"
                      : "— half-hour steps, 0.5 to 8 hrs"}
                </span>
              </label>
              <table className="toa-mini-table toa-modify-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Day</th>
                    <th className="toa-num">Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {dayKeys.map((k) => {
                    const bad = dayIsInvalid(k);
                    const optional = isOptionalDay(k);
                    if (unpaidSel) {
                      const holiday = isKnownHoliday(k);
                      return (
                        <tr key={k}>
                          <td>{fmtDate(k)}</td>
                          <td>
                            {weekdayLabel(k)}
                            {holiday && (
                              <span className="toa-modal-opt">
                                {" "}· {holidayNames.get(k)} (holiday — skipped)
                              </span>
                            )}
                          </td>
                          <td className="toa-num">{holiday ? "—" : `${UNPAID_DAY_HOURS} (full day)`}</td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={k}>
                        <td>{fmtDate(k)}</td>
                        <td>
                          {weekdayLabel(k)}
                          {optional && (
                            <span className="toa-modal-opt">
                              {" "}· {holidayNames.get(k) ?? "Weekend"} (optional)
                            </span>
                          )}
                        </td>
                        <td className="toa-num">
                          <input
                            type="number"
                            step={DAY_STEP}
                            min={optional ? 0 : MIN_DAY_HOURS}
                            max={MAX_DAY_HOURS}
                            className={`toa-hin${bad ? " over" : ""}`}
                            value={hours[k] ?? ""}
                            disabled={submitting}
                            onChange={(e) => {
                              touchedDaysRef.current.add(k);
                              setHours((prev) => ({ ...prev, [k]: e.target.value }));
                            }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {invalidDays.length > 0 && (
                <div className="toa-modal-err">
                  <AlertTriangle size={15} />
                  {ALLOW_WEEKEND_HOLIDAY_DAYS
                    ? (invalidDays.length === 1
                      ? `${fmtDate(invalidDays[0])} needs 0.5 to 8 hrs in half-hour steps (0 is allowed on weekend and holiday days).`
                      : `${invalidDays.length} days need 0.5 to 8 hrs in half-hour steps (0 is allowed on weekend and holiday days).`)
                    : (invalidDays.length === 1
                      ? `${fmtDate(invalidDays[0])} needs 0.5 to 8 hrs in half-hour steps.`
                      : `${invalidDays.length} days need 0.5 to 8 hrs in half-hour steps.`)}
                </div>
              )}
            </div>
          )}

          {/* ── Summary ───────────────────────────────────────────────────── */}
          {picked && dayKeys.length > 0 && (
            <div className="toa-modify-summary">
              <div className="toa-ms-line">
                Total <b>{fmtHoursNum(totalHours)}</b> hrs
                {requestedDayKeys.length > 1 ? ` · ${requestedDayKeys.length} days` : ""}
              </div>
              {unpaidSel ? (
                <div className="toa-ms-line">Unpaid · no balance</div>
              ) : (
                <div className={`toa-ms-line${balanceShort ? " short" : ""}`}>
                  Balance after:{" "}
                  <b>{balanceAfter == null ? "—" : `${fmtHoursNum(balanceAfter)} hrs`}</b>
                </div>
              )}
            </div>
          )}

          {balanceShort && selectedRow && (
            <div className="toa-modal-warn">
              <AlertTriangle size={16} />
              <div>
                This is more {selectedRow.categoryName} than {firstName(picked?.name ?? "")} has
                available. You can still submit — the API has the final say on balances and
                annual caps.
              </div>
            </div>
          )}

          {/* ── 5. Notes ──────────────────────────────────────────────────── */}
          {picked && (
            <div className="toa-newreq-sec">
              <label className="toa-modal-label">
                Notes <span className="toa-modal-opt">— optional, shared with the employee</span>
              </label>
              <textarea
                className="toa-modal-textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Why is this being submitted for them?"
                rows={3}
                disabled={submitting}
              />
            </div>
          )}

          {/* ── 6. Approve now ────────────────────────────────────────────── */}
          {picked?.canApprove && (
            <label className="toa-check-line">
              <input
                type="checkbox"
                checked={approveNow}
                disabled={submitting}
                onChange={(e) => setApproveNow(e.target.checked)}
              />
              <span>
                <b>Approve now</b>
                <span className="toa-check-sub">
                  Creates and approves it in one step, recorded against your name. Leave it off
                  to send it through the normal queue.
                </span>
              </span>
            </label>
          )}

          {apiError && (
            <div className="toa-modal-err">
              <AlertTriangle size={15} />
              {apiError}
            </div>
          )}
        </div>

        <div className="toa-modal-foot">
          <button className="toa-btn toa-btn-ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="toa-btn toa-btn-primary"
            onClick={() => handleSubmit()}
            disabled={!canSubmit}
          >
            {submitting
              ? "Submitting…"
              : approveNow && picked?.canApprove
                ? "Submit & approve"
                : "Submit request"}
          </button>
        </div>
      </div>
    </div>
    {paidGate && picked && (
      <PaidOverrideModal
        mode="submit"
        employeeName={picked.name}
        error={paidGate}
        onConfirm={() => handleSubmit(true)}
        onClose={() => setPaidGate(null)}
      />
    )}
    </>
  );
};

const RequestRow: React.FC<RequestRowProps> = ({
  req, showBulk, expanded, detail, loadingDetail, selected, removing,
  detailNote, balances, loadingBalances, balancesError, allowEditApproved,
  onToggleExpand, onToggleSelect, onApprove, onDeny, onModifyHours, onDenyApproved,
  onDetailNoteChange, onRetryBalances,
}) => {
  const colSpan = showBulk ? 9 : 8;
  const actionable = req.status === "PENDING" && canActOn(req);
  /**
   * Approved requests stay correctable — but only for a viewer holding the dedicated
   * "Edit approved time off" permission, and only when the server agrees for this request.
   */
  const correctable =
    req.status === "APPROVED" && canActOn(req) && allowEditApproved && canEditApprovedOn(req);
  /** An on-behalf request was never submitted by the employee, so the timeline says so instead. */
  const submittedOnBehalf = (detail?.approvalLog ?? []).some(
    (l) => l.action === "SUBMITTED_FOR_EMPLOYEE"
  );
  /**
   * Unpaid Time (TAS-586): who let it through while paid time was still available. The column on
   * the request is the record; the approval-log note may or may not carry the same line.
   */
  const overrideByID = detail?.paidExhaustedOverrideAdminEmployeeID ?? req.paidExhaustedOverrideAdminEmployeeID;
  const overrideBy = detail?.paidExhaustedOverrideBy ?? req.paidExhaustedOverrideBy
    ?? (overrideByID != null ? `Admin #${overrideByID}` : null);

  return (
    <>
      <tr className={`toa-req-row${expanded ? " open" : ""}${removing ? " toa-removing" : ""}`}>
        {showBulk && (
          <td className="toa-td-check">
            {actionable && (
              <input
                type="checkbox"
                checked={selected}
                onChange={onToggleSelect}
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </td>
        )}
        <td className="toa-emp-td">
          <div className="toa-emp-cell" onClick={onToggleExpand}>
            <div className="toa-av">{getInitials(req.employeeName)}</div>
            <div className="toa-emp-text">
              <div className="toa-emp-name">{req.employeeName}</div>
              <EmployeeSubLine supervisorName={req.supervisorName} altEmployeeID={req.altEmployeeID} />
            </div>
          </div>
        </td>
        <td className="toa-id-cell toa-col-id">
          {req.altEmployeeID ? `#${req.altEmployeeID}` : "—"}
        </td>
        <td className="toa-cat-cell">
          <CategoryChip categoryName={req.categoryName} />
          {isUnpaid(req) && <UnpaidTag />}
        </td>
        <td className="toa-dates-cell">
          {fmtDate(req.startDate)} – {fmtDate(req.endDate)}
          <span className="toa-relsub" title={`Submitted ${fmtDate(req.submittedAt)}`}>
            {relativeTime(req.submittedAt)}
          </span>
        </td>
        <td className="toa-hrs-cell">{req.totalHours}h</td>
        <td className="toa-sub-cell toa-col-sub">{fmtDate(req.submittedAt)}</td>
        <td className="toa-status-cell"><StatusPill status={req.status} /></td>
        <td className="toa-acts-cell">
          <div className="toa-rowacts">
            {actionable && (
              <>
                <button
                  className="toa-qbtn toa-qbtn-deny"
                  onClick={(e) => { e.stopPropagation(); onDeny(req); }}
                >
                  <X size={14} /> Deny
                </button>
                <button
                  className="toa-qbtn toa-qbtn-approve"
                  onClick={(e) => { e.stopPropagation(); onApprove(req); }}
                >
                  <Check size={14} /> Approve
                </button>
              </>
            )}
            <button className="toa-chevbtn" onClick={onToggleExpand} aria-label="Expand details">
              <ChevronDown size={16} className="toa-chev-icon" />
            </button>
          </div>
        </td>
      </tr>

      {expanded && (
        <tr className={`toa-detail-row${removing ? " toa-removing" : ""}`}>
          <td colSpan={colSpan}>
            <div className="toa-detail-wrap">
              {loadingDetail ? (
                <div className="toa-detail-loading">Loading details…</div>
              ) : detail ? (
                <>
                  <BalancesBand
                    req={req}
                    rows={balances}
                    loading={loadingBalances}
                    error={balancesError}
                    onRetry={onRetryBalances}
                  />
                  <div className="toa-detail-grid">
                    {/* Left — Day-by-day breakdown */}
                    <div>
                      <div className="toa-dh">
                        <Calendar size={15} />
                        Day-by-day breakdown
                      </div>
                      {detail.details && detail.details.length > 0 ? (
                        <table className="toa-mini-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th className="toa-num">Hours</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detail.details.map((d) => (
                              <tr key={d.requestDate}>
                                <td>{fmtDate(d.requestDate)}</td>
                                <td className="toa-num">{fmtHoursNum(d.hours)}h</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td>Total</td>
                              <td className="toa-num">
                                {fmtHoursNum(detail.details.reduce((s, d) => s + d.hours, 0))}h
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      ) : (
                        <p className="toa-muted">No day breakdown available.</p>
                      )}
                      {/* The old single-number chip row lived here; the balances band above
                          supersedes it with per-category Available/Accrued/Used/Pending. */}
                    </div>

                    {/* Right — Approval history */}
                    <div>
                      <div className="toa-dh">
                        <Clock size={15} />
                        Approval history
                      </div>
                      <ul className="toa-history">
                        {!submittedOnBehalf && (
                          <li>
                            <div className="toa-hdot toa-hdot-submitted" />
                            <div className="toa-htxt">
                              <b>Submitted by {req.employeeName}</b>
                              <div className="toa-hmeta">{fmtDate(req.submittedAt)}</div>
                            </div>
                          </li>
                        )}
                        {detail.approvalLog && detail.approvalLog.length > 0 ? (
                          detail.approvalLog.map((log, i) => (
                            <li key={i}>
                              <div className={`toa-hdot toa-hdot-${actionDotClass(log.action)}`} />
                              <div className="toa-htxt">
                                {log.action === "SUBMITTED_FOR_EMPLOYEE" ? (
                                  <b>
                                    Submitted by{" "}
                                    {log.approverName ?? `Admin #${log.actedByAdminEmployeeID}`}{" "}
                                    on behalf of the employee
                                  </b>
                                ) : (
                                  <>
                                    <b>{actionLabel(log.action)}</b>
                                    {" by "}
                                    {log.approverName ?? `Admin #${log.actedByAdminEmployeeID}`}
                                  </>
                                )}
                                <div className="toa-hmeta">{fmtDateTime(log.actedAt)}</div>
                                {log.notes && (
                                  <div className="toa-hnote">"{log.notes}"</div>
                                )}
                              </div>
                            </li>
                          ))
                        ) : req.status === "PENDING" ? (
                          <li>
                            <div className="toa-hdot" />
                            <div className="toa-htxt">
                              <span className="toa-muted">
                                {actionable
                                  ? "Awaiting your decision · pending review"
                                  : "Awaiting approval · pending review"}
                              </span>
                            </div>
                          </li>
                        ) : null}
                      </ul>
                      {overrideBy && (
                        <div className="toa-override-note">
                          <AlertTriangle size={14} />
                          Paid-time rule overridden by {overrideBy}
                        </div>
                      )}
                    </div>
                  </div>

                  {actionable && (
                    <div className="toa-detail-foot">
                      <input
                        type="text"
                        className="toa-note-in"
                        placeholder="Add a note the employee will see (optional)…"
                        value={detailNote}
                        onChange={(e) => onDetailNoteChange(e.target.value)}
                      />
                      <button
                        className="toa-btn toa-btn-deny-solid"
                        onClick={() => onDeny(req, detailNote)}
                      >
                        <X size={14} /> Deny
                      </button>
                      <button
                        className="toa-btn toa-btn-approve-solid"
                        onClick={() => onApprove(req, detailNote)}
                      >
                        <Check size={14} /> Approve
                      </button>
                    </div>
                  )}

                  {correctable && (
                    <div className="toa-detail-foot">
                      <span className="toa-foot-hint">
                        Already approved — payroll can still correct it.
                      </span>
                      <button className="toa-btn" onClick={() => onModifyHours(req)}>
                        <Pencil size={14} /> Modify hours
                      </button>
                      <button
                        className="toa-btn toa-btn-deny-solid"
                        onClick={() => onDenyApproved(req)}
                      >
                        <X size={14} /> Deny approved
                      </button>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ─── Calendar Helpers ─────────────────────────────────────────────────────────

function expandDateRange(startDate: string, endDate: string): string[] {
  const dates: string[] = [];
  const cur = new Date(startDate + "T00:00:00");
  const end = new Date(endDate + "T00:00:00");
  while (cur <= end) {
    dates.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

function buildDayMap(requests: CalendarRequestEntry[]): Record<string, CalendarDay> {
  const map: Record<string, CalendarDay> = {};
  for (const req of requests) {
    const days = expandDateRange(req.startDate, req.endDate);
    const hoursPerDay = days.length > 0 ? req.totalHours / days.length : req.totalHours;
    for (const date of days) {
      if (!map[date]) map[date] = { date, requests: [] };
      map[date].requests.push({
        timeOffRequestID: req.timeOffRequestID,
        employeeID:       req.employeeID,
        employeeName:     req.employeeName,
        categoryName:     req.categoryName,
        hours:            Math.round(hoursPerDay * 100) / 100,
        status:           req.status,
        canAct:           req.canAct,
      });
    }
  }
  return map;
}

// ─── Calendar Popover ─────────────────────────────────────────────────────────

interface PopoverData {
  entry: CalendarRequestEntry;
  x: number;
  y: number;
}

interface CalendarPopoverProps {
  data: PopoverData;
  onClose: () => void;
  onApprove: (entry: CalendarRequestEntry) => void;
  onDeny: (entry: CalendarRequestEntry) => void;
}

const CalendarPopover: React.FC<CalendarPopoverProps> = ({ data, onClose, onApprove, onDeny }) => {
  const { entry, x, y } = data;
  const popRef = useRef<HTMLDivElement>(null);

  const left = Math.min(x, window.innerWidth - 340);
  const top  = Math.min(y, window.innerHeight - 340);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="toa-popover" style={{ top, left }} ref={popRef}>
      <button className="toa-po-close" onClick={onClose}><X size={15} /></button>
      <div className="toa-po-head">
        <div className="toa-av toa-av-sm">{getInitials(entry.employeeName)}</div>
        <div>
          <div className="toa-po-name">{entry.employeeName}</div>
          <div className="toa-po-sub">Time Off Request</div>
        </div>
      </div>
      <div className="toa-po-body">
        <div className="toa-po-row">
          <span className="toa-po-label">Category</span>
          <span>
            <CategoryChip categoryName={entry.categoryName ?? "—"} />
            {isUnpaid(entry) && <UnpaidTag />}
          </span>
        </div>
        <div className="toa-po-row">
          <span className="toa-po-label">Dates</span>
          <span className="toa-mono">{fmtDate(entry.startDate)} – {fmtDate(entry.endDate)}</span>
        </div>
        <div className="toa-po-row">
          <span className="toa-po-label">Hours</span>
          <strong>{entry.totalHours}h</strong>
        </div>
        <div className="toa-po-row">
          <span className="toa-po-label">Status</span>
          <StatusPill status={entry.status} />
        </div>
      </div>
      {entry.status === "PENDING" && canActOn(entry) && (
        <div className="toa-po-foot">
          <button className="toa-btn toa-btn-deny-outline" onClick={() => onDeny(entry)}>
            <X size={14} /> Deny
          </button>
          <button className="toa-btn toa-btn-approve-outline" onClick={() => onApprove(entry)}>
            <Check size={14} /> Approve
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Calendar View ────────────────────────────────────────────────────────────

interface CalendarViewProps {
  onCountChange: () => void;
}

const CalendarView: React.FC<CalendarViewProps> = ({ onCountChange }) => {
  const { setLoading, createPopup } = useSiteContext();
  const today = new Date();
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [dayMap, setDayMap]       = useState<Record<string, CalendarDay>>({});
  const [holidays, setHolidays]   = useState<CalendarHoliday[]>([]);
  const [requestMap, setRequestMap] = useState<Record<number, CalendarRequestEntry>>({});
  const [calStatusFilter, setCalStatusFilter] = useState("ALL");
  const [popover, setPopover]       = useState<PopoverData | null>(null);
  const [actionModal, setActionModal] = useState<{ action: "approve" | "deny"; req: CalendarRequestEntry } | null>(null);
  /** An approval refused with PAID_NOT_EXHAUSTED, waiting on "Approve anyway". */
  const [approveOverride, setApproveOverride] = useState<{
    req: CalendarRequestEntry; notes: string; error: unknown;
  } | null>(null);

  const loadCalendar = useCallback(async (y: number, m: number) => {
    setLoading(true);
    try {
      const first   = `${y}-${String(m + 1).padStart(2, "0")}-01`;
      const lastDay = new Date(y, m + 1, 0).getDate();
      const last    = `${y}-${String(m + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
      const data    = await API.getCalendarRequests(first, last);
      const reqs    = Array.isArray(data.requests)  ? data.requests  : [];
      const hols    = Array.isArray(data.holidays)  ? data.holidays  : [];

      const filtered = calStatusFilter === "ALL" ? reqs : reqs.filter((r) => r.status === calStatusFilter);
      setDayMap(buildDayMap(filtered));
      setHolidays(hols);

      const rm: Record<number, CalendarRequestEntry> = {};
      reqs.forEach((r) => { rm[r.timeOffRequestID] = r; });
      setRequestMap(rm);
    } catch (err) {
      console.error("Failed to load calendar:", err);
    } finally {
      setLoading(false);
    }
  }, [setLoading, calStatusFilter]);

  useEffect(() => { loadCalendar(year, month); }, [year, month, loadCalendar]);

  const prevMonth = () => {
    if (month === 0) { setYear((y) => y - 1); setMonth(11); }
    else setMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setYear((y) => y + 1); setMonth(0); }
    else setMonth((m) => m + 1);
  };

  const monthName  = new Date(year, month, 1).toLocaleString("default", { month: "long" });
  const firstDow   = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayStr   = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const holidayMap: Record<string, CalendarHoliday> = {};
  holidays.forEach((h) => { holidayMap[h.holidayDate.slice(0, 10)] = h; });

  const cells: Array<{ day: number | null; date: string | null }> = [];
  for (let i = 0; i < firstDow; i++) cells.push({ day: null, date: null });
  for (let d = 1; d <= daysInMonth; d++) {
    const date = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ day: d, date });
  }

  const handleCalAction = async (notes: string) => {
    if (!actionModal) return;
    try {
      if (actionModal.action === "approve") {
        await API.approveTimeOffRequest(actionModal.req.timeOffRequestID, notes);
      } else {
        await API.denyTimeOffRequest(actionModal.req.timeOffRequestID, notes);
      }
      setActionModal(null);
      setPopover(null);
      await loadCalendar(year, month);
      onCountChange();
    } catch (err) {
      console.error("Calendar action failed:", err);
      // Unpaid Time whose paid-time rule failed: offer the one-request override instead.
      if (actionModal.action === "approve" && isPaidNotExhaustedError(err)) {
        setApproveOverride({ req: actionModal.req, notes, error: err });
        setActionModal(null);
        setPopover(null);
        return;
      }
      createPopup(
        "Error",
        actionModal.action === "approve"
          ? approveFailureMessage(err, "Failed to process request. Please try again.")
          : "Failed to process request. Please try again."
      );
    }
  };

  const handleCalApproveOverride = async () => {
    if (!approveOverride) return;
    const { req, notes } = approveOverride;
    try {
      await API.approveTimeOffRequest(req.timeOffRequestID, notes, { overrideExhausted: true });
      setApproveOverride(null);
      await loadCalendar(year, month);
      onCountChange();
    } catch (err) {
      console.error("Calendar override approve failed:", err);
      setApproveOverride(null);
      createPopup("Error", approveFailureMessage(err, "Failed to process request. Please try again."));
    }
  };

  const openPopover = (e: React.MouseEvent, req: CalendarDayRequest) => {
    e.stopPropagation();
    const full = requestMap[req.timeOffRequestID];
    if (!full) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setPopover({ entry: full, x: rect.left, y: rect.bottom + 6 });
  };

  const asRequestSummary = (entry: CalendarRequestEntry): TimeOffRequestSummary => ({
    timeOffRequestID: entry.timeOffRequestID,
    employeeID:       entry.employeeID,
    altEmployeeID:    null,
    employeeName:     entry.employeeName,
    timeOffCategoryID: 0,
    categoryName:     entry.categoryName ?? "",
    startDate:        entry.startDate,
    endDate:          entry.endDate,
    totalHours:       entry.totalHours,
    status:           entry.status,
    notes:            null,
    submittedAt:      "",
    canAct:           entry.canAct,
    isUnpaid:         entry.isUnpaid,
  });

  return (
    <div className="toa-calendar">
      {/* Filter bar */}
      <div className="toa-filterbar">
        <div className="toa-field">
          <span className="toa-flab">Status</span>
          <select
            className="toa-select"
            value={calStatusFilter}
            onChange={(e) => setCalStatusFilter(e.target.value)}
          >
            <option value="ALL">All</option>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
          </select>
        </div>
        <button className="toa-btn" onClick={() => loadCalendar(year, month)}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {/* Toolbar */}
      <div className="toa-cal-toolbar">
        <button className="toa-cal-nav-btn" onClick={prevMonth}><ChevronLeft size={18} /></button>
        <button className="toa-cal-nav-btn" onClick={nextMonth}><ChevronRight size={18} /></button>
        <span className="toa-cal-month">{monthName} {year}</span>
        <div className="toa-cal-legend">
          <span><i className="toa-legend-i toa-legend-pending" /> Pending</span>
          <span><i className="toa-legend-i toa-legend-approved" /> Approved</span>
          <span><i className="toa-legend-i toa-legend-holiday" /> Holiday</span>
        </div>
      </div>

      {/* Grid */}
      <div className="toa-cal-grid">
        <div className="toa-cal-dows">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((dow) => (
            <div key={dow}>{dow}</div>
          ))}
        </div>
        <div className="toa-cal-body">
          {cells.map((cell, i) => {
            if (!cell.date || !cell.day) {
              return <div key={i} className="toa-cal-cell toa-cal-out" />;
            }
            const cd      = dayMap[cell.date];
            const holiday = holidayMap[cell.date];
            const isToday = cell.date === todayStr;
            const evts    = cd ? cd.requests : [];
            const shown   = evts.slice(0, 2);
            const moreCount = evts.length - shown.length;

            return (
              <div key={i} className={`toa-cal-cell${isToday ? " toa-cal-today" : ""}`}>
                <div className={`toa-dnum${isToday ? " toa-dnum-today" : ""}`}>
                  {cell.day}
                </div>
                {holiday && (
                  <div className="toa-ev toa-ev-holiday">
                    <span className="toa-ev-dot" />
                    {holiday.name}
                  </div>
                )}
                {shown.map((r) => (
                  <div
                    key={r.timeOffRequestID}
                    className={`toa-ev toa-ev-${r.status.toLowerCase()}`}
                    onClick={(e) => openPopover(e, r)}
                  >
                    <span className="toa-ev-dot" />
                    {r.employeeName} · {r.categoryName}
                  </div>
                ))}
                {moreCount > 0 && (
                  <div className="toa-ev-more">+{moreCount} more</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {popover && (
        <CalendarPopover
          data={popover}
          onClose={() => setPopover(null)}
          onApprove={(entry) => { setPopover(null); setActionModal({ action: "approve", req: entry }); }}
          onDeny={(entry)    => { setPopover(null); setActionModal({ action: "deny",    req: entry }); }}
        />
      )}

      {actionModal && (
        <ActionModal
          action={actionModal.action}
          request={asRequestSummary(actionModal.req)}
          onConfirm={handleCalAction}
          onClose={() => setActionModal(null)}
        />
      )}

      {approveOverride && (
        <PaidOverrideModal
          mode="approve"
          employeeName={approveOverride.req.employeeName}
          error={approveOverride.error}
          onConfirm={handleCalApproveOverride}
          onClose={() => setApproveOverride(null)}
        />
      )}
    </div>
  );
};

// ─── Cash Out Queue ───────────────────────────────────────────────────────────

interface CashOutQueueProps {
  onCountChange: () => void;
}

const CashOutQueue: React.FC<CashOutQueueProps> = ({ onCountChange }) => {
  const { createPopup } = useSiteContext();
  const [requests, setRequests] = useState<CashOutRequestSummary[]>([]);
  const [loading, setLoading]   = useState(false);
  const [statusFilter, setStatusFilter] = useState<TimeOffRequestStatus | "ALL">("PENDING");
  const [actionModal, setActionModal] = useState<{
    action: "approve" | "deny";
    req: CashOutRequestSummary;
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await API.getCashOutRequests({
        status: statusFilter === "ALL" ? undefined : statusFilter,
      });
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load cash out requests:", err);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (notes: string) => {
    if (!actionModal) return;
    try {
      if (actionModal.action === "approve") {
        await API.approveCashOutRequest(actionModal.req.cashOutRequestID, notes);
      } else {
        await API.denyCashOutRequest(actionModal.req.cashOutRequestID, notes);
      }
      setActionModal(null);
      await load();
      onCountChange();
    } catch (err) {
      console.error("Cash out action failed:", err);
      createPopup("Error", "Failed to process request. Please try again.");
    }
  };

  return (
    <div>
      <div className="toa-filterbar">
        <div className="toa-field">
          <span className="toa-flab">Status</span>
          <select
            className="toa-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as TimeOffRequestStatus | "ALL")}
          >
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="DENIED">Denied</option>
            <option value="ALL">All</option>
          </select>
        </div>
        <button className="toa-btn" onClick={load}><RefreshCw size={16} /> Refresh</button>
      </div>

      {loading ? (
        <div className="toa-loading">Loading…</div>
      ) : requests.length === 0 ? (
        <div className="toa-empty">
          <DollarSign size={34} />
          <div className="toa-empty-title">No cash out requests</div>
          <div className="toa-empty-sub">No requests found for the selected filters.</div>
        </div>
      ) : (
        <div className="toa-qcard">
          <table className="toa-qtable">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Category</th>
                <th className="toa-num">Hours</th>
                <th>Submitted</th>
                <th>Status</th>
                <th className="toa-acts">Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => (
                <tr key={req.cashOutRequestID} className="toa-req-row">
                  <td className="toa-emp-td">
                    <div className="toa-emp-cell">
                      <div className="toa-av">{getInitials(req.employeeName)}</div>
                      <div className="toa-emp-name">{req.employeeName}</div>
                    </div>
                  </td>
                  <td className="toa-cat-cell"><CategoryChip categoryName={req.categoryName} /></td>
                  <td className="toa-num toa-hrs-cell">{req.hours}h</td>
                  <td className="toa-sub-cell">{fmtDate(req.submittedAt)}</td>
                  <td className="toa-status-cell"><StatusPill status={req.status} /></td>
                  <td className="toa-acts-cell">
                    {req.status === "PENDING" && canActOn(req) && (
                      <div className="toa-rowacts">
                        <button
                          className="toa-qbtn toa-qbtn-deny"
                          onClick={() => setActionModal({ action: "deny", req })}
                        >
                          <X size={14} /> Deny
                        </button>
                        <button
                          className="toa-qbtn toa-qbtn-approve"
                          onClick={() => setActionModal({ action: "approve", req })}
                        >
                          <Check size={14} /> Approve
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {actionModal && (
        <ActionModal
          action={actionModal.action}
          request={{
            timeOffRequestID:  actionModal.req.cashOutRequestID,
            employeeID:        actionModal.req.employeeID,
            altEmployeeID:     actionModal.req.altEmployeeID,
            employeeName:      actionModal.req.employeeName,
            timeOffCategoryID: actionModal.req.timeOffCategoryID,
            categoryName:      actionModal.req.categoryName,
            startDate:         actionModal.req.submittedAt,
            endDate:           actionModal.req.submittedAt,
            totalHours:        actionModal.req.hours,
            status:            actionModal.req.status,
            notes:             actionModal.req.notes,
            submittedAt:       actionModal.req.submittedAt,
          }}
          onConfirm={handleAction}
          onClose={() => setActionModal(null)}
        />
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

// Cash Out is not fully implemented yet — flip to true to restore the tab.
const SHOW_CASH_OUT_TAB = false;

const TimeOffRequestApprovalPage: React.FC = () => {
  const {
    userRole, createPopup, setPendingTimeOffCount, pendingDeepLink, pendingTimeOffCount,
  } = useSiteContext();
  const isAdmin = userRole.admin;
  /** Post-approval corrections are their own permission — most roles hold approve/deny. */
  const allowEditApproved = can(userRole, "editApprovedTimeOffRequests");
  /** Raising a request for someone else (Payroll / HR) — hides the whole "New request" flow. */
  const allowSubmitForEmployee = can(userRole, "submitTimeOffForEmployee");

  const [mainTab,     setMainTab]     = useState<MainTab>("queue");
  const [queueSubTab, setQueueSubTab] = useState<QueueSubTab>("timeOff");

  // API filters
  const [statusFilter,    setStatusFilter]    = useState<TimeOffRequestStatus | "ALL">("PENDING");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter,   setEndDateFilter]   = useState("");

  // Client-side filters
  const [searchQuery,    setSearchQuery]    = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  // Data
  const [requests, setRequests] = useState<TimeOffRequestSummary[]>([]);
  const [loading,  setLoading]  = useState(false);

  // Row state
  const [expandedID,      setExpandedID]      = useState<number | null>(null);
  const [detailCache,     setDetailCache]     = useState<Record<number, TimeOffRequestDetail>>({});
  const [loadingDetailID, setLoadingDetailID] = useState<number | null>(null);
  const [selectedIDs,     setSelectedIDs]     = useState<Set<number>>(new Set());
  const [removingIDs,     setRemovingIDs]     = useState<Set<number>>(new Set());
  const [detailNotes,     setDetailNotes]     = useState<Record<number, string>>({});

  // Balances are per EMPLOYEE, not per request — cache by employeeID so two requests from the
  // same person cost one fetch.
  const [balanceCache,   setBalanceCache]   = useState<Record<number, EmployeeTimeOffBalanceRow[]>>({});
  const [loadingBalEmpID, setLoadingBalEmpID] = useState<number | null>(null);
  const [balanceErrEmpIDs, setBalanceErrEmpIDs] = useState<Set<number>>(new Set());

  // Session stats
  const [clearedCount, setClearedCount] = useState(0);

  // Modal
  const [actionModal, setActionModal] = useState<{
    action: ActionModalAction;
    req: TimeOffRequestSummary;
    initialNote?: string;
  } | null>(null);
  /** Modify-hours only opens off an expanded row, so the detail is always already loaded. */
  const [modifyModal, setModifyModal] = useState<TimeOffRequestSummary | null>(null);
  /** The on-behalf create modal. Opens empty — it loads its own roster and balances. */
  const [newRequestOpen, setNewRequestOpen] = useState(false);
  /**
   * Unpaid Time (TAS-586): an approval the API refused with PAID_NOT_EXHAUSTED, held while the
   * approver decides whether to approve anyway. One request at a time — bulk never overrides.
   */
  const [approveOverride, setApproveOverride] = useState<{
    req: TimeOffRequestSummary; notes: string; error: unknown;
  } | null>(null);

  // Pagination
  const [currentPage,  setCurrentPage]  = useState(1);
  const [rowsPerPage,  setRowsPerPage]  = useState(25);

  const deepLinkConsumed = useRef(false);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setCurrentPage(1);
    try {
      const filters: TimeOffRequestFilters = {
        status:    statusFilter === "ALL" ? undefined : statusFilter,
        startDate: startDateFilter || undefined,
        endDate:   endDateFilter   || undefined,
      };
      const data = await API.getTimeOffRequests(filters);
      const list = Array.isArray(data) ? data : [];
      setRequests(list);
      // Drop selections the reload invalidated — acted on elsewhere, filtered out, or no longer
      // ours to approve — so a stale id can never ride along in a bulk approve.
      const selectable = new Set(
        list.filter((r) => r.status === "PENDING" && canActOn(r)).map((r) => r.timeOffRequestID)
      );
      setSelectedIDs((prev) => new Set(Array.from(prev).filter((id) => selectable.has(id))));
    } catch (err) {
      console.error("Failed to load time off requests:", err);
      setRequests([]);
      setSelectedIDs(new Set());
    } finally {
      setLoading(false);
    }
  }, [statusFilter, startDateFilter, endDateFilter]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  useEffect(() => {
    if (deepLinkConsumed.current || !pendingDeepLink?.requestId) return;
    deepLinkConsumed.current = true;
    setExpandedID(pendingDeepLink.requestId);
    setStatusFilter("ALL");
  }, [pendingDeepLink]);

  const loadDetail = useCallback(async (id: number) => {
    if (detailCache[id]) return;
    setLoadingDetailID(id);
    try {
      const detail = await API.getTimeOffRequestById(id);
      setDetailCache((prev) => ({ ...prev, [id]: detail }));
    } catch (err) {
      console.error("Failed to load request detail:", err);
    } finally {
      setLoadingDetailID(null);
    }
  }, [detailCache]);

  /** Fetches an employee's per-category balances once and caches them. */
  const loadBalances = useCallback(async (employeeID: number, force = false) => {
    if (!force && balanceCache[employeeID]) return;
    setLoadingBalEmpID(employeeID);
    setBalanceErrEmpIDs((prev) => { const n = new Set(prev); n.delete(employeeID); return n; });
    try {
      const rows = await getEmployeeTimeOffBalance(employeeID);
      setBalanceCache((prev) => ({ ...prev, [employeeID]: Array.isArray(rows) ? rows : [] }));
    } catch (err) {
      console.error("Failed to load time off balances:", err);
      setBalanceErrEmpIDs((prev) => new Set([...prev, employeeID]));
    } finally {
      setLoadingBalEmpID(null);
    }
  }, [balanceCache]);

  const handleToggleExpand = (id: number) => {
    if (expandedID === id) { setExpandedID(null); return; }
    setExpandedID(id);
    loadDetail(id);
    const req = requests.find((r) => r.timeOffRequestID === id);
    if (req) loadBalances(req.employeeID);
  };

  const handleToggleSelect = (id: number) => {
    setSelectedIDs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const refreshCount = useCallback(async () => {
    try {
      const count = await API.getPendingTimeOffCount();
      setPendingTimeOffCount(count);
    } catch { /* ignore */ }
  }, [setPendingTimeOffCount]);

  /** After an approve/deny lands: drop the stale caches and take the row out of the queue. */
  const finishAction = async (req: TimeOffRequestSummary) => {
    const id = req.timeOffRequestID;
    setDetailCache((prev) => { const n = { ...prev }; delete n[id]; return n; });
    // A denial credits the hours back, so this employee's balances are now stale.
    const empID = req.employeeID;
    setBalanceCache((prev) => { const n = { ...prev }; delete n[empID]; return n; });

    if (statusFilter !== "ALL") {
      setRemovingIDs((prev) => new Set([...prev, id]));
      setTimeout(() => {
        setRemovingIDs((prev) => { const n = new Set(prev); n.delete(id); return n; });
        setRequests((prev) => prev.filter((r) => r.timeOffRequestID !== id));
        setClearedCount((c) => c + 1);
        refreshCount();
      }, 420);
    } else {
      await loadRequests();
      setClearedCount((c) => c + 1);
      refreshCount();
    }
  };

  const handleAction = async (notes: string) => {
    if (!actionModal) return;
    const req = actionModal.req;
    const id = req.timeOffRequestID;
    try {
      if (actionModal.action === "approve") {
        await API.approveTimeOffRequest(id, notes);
      } else if (actionModal.action === "denyApproved") {
        await API.denyApprovedTimeOffRequest(id, notes.trim());
      } else {
        await API.denyTimeOffRequest(id, notes);
      }
      setActionModal(null);
      await finishAction(req);
    } catch (err) {
      console.error("Action failed:", err);
      // Unpaid Time whose paid-time rule failed on the re-check: offer "Approve anyway".
      if (actionModal.action === "approve" && isPaidNotExhaustedError(err)) {
        setActionModal(null);
        setApproveOverride({ req, notes, error: err });
        return;
      }
      // denyApproved answers 409/422 with a real explanation ("already denied", "not approved").
      // Showing the generic line instead would hide the only thing that tells payroll what to do.
      const message = actionModal.action === "approve"
        ? approveFailureMessage(err, "Failed to process request. Please try again.")
        : err instanceof API.TimeOffActionError && err.message
          ? err.message
          : "Failed to process request. Please try again.";
      createPopup("Error", message);
    }
  };

  /** "Approve anyway" from the paid-time confirm — re-sends this one approval with the override. */
  const handleApproveOverride = async () => {
    if (!approveOverride) return;
    const { req, notes } = approveOverride;
    try {
      await API.approveTimeOffRequest(req.timeOffRequestID, notes, { overrideExhausted: true });
      setApproveOverride(null);
      await finishAction(req);
    } catch (err) {
      console.error("Override approve failed:", err);
      setApproveOverride(null);
      createPopup("Error", approveFailureMessage(err, "Failed to process request. Please try again."));
    }
  };

  /** After a successful hours change: drop the caches the change invalidated and reload. */
  const handleHoursModified = async (req: TimeOffRequestSummary, totalHours: number) => {
    const id = req.timeOffRequestID;
    setModifyModal(null);
    setDetailCache((prev) => { const n = { ...prev }; delete n[id]; return n; });
    setBalanceCache((prev) => { const n = { ...prev }; delete n[req.employeeID]; return n; });
    await loadRequests();
    await refreshCount();
    createPopup(
      "Hours updated",
      `${req.employeeName}'s ${req.categoryName} request is now ${totalHours} hrs. They have been notified.`,
    );
  };

  /** A new on-behalf request lands in the queue (or straight into Approved), so reload both. */
  const handleNewRequestSubmitted = async (employeeName: string, approved: boolean, approveError?: string) => {
    setNewRequestOpen(false);
    // The submit debits the category balance, so this employee's cached rows are stale.
    setBalanceCache({});
    await loadRequests();
    await refreshCount();
    createPopup(
      "Request submitted",
      approved
        ? `Request submitted for ${employeeName} and approved.`
        : approveError
          ? `Request submitted for ${employeeName}, but it could not be approved: ${approveError} It is waiting in the queue.`
          : `Request submitted for ${employeeName}.`,
    );
  };

  const handleBulkApprove = async () => {
    if (selectedIDs.size === 0) return;
    // Only send ids the API would still accept: pending, and actionable by this admin.
    const ids = requests
      .filter((r) => selectedIDs.has(r.timeOffRequestID) && r.status === "PENDING" && canActOn(r))
      .map((r) => r.timeOffRequestID);
    if (ids.length === 0) { setSelectedIDs(new Set()); return; }
    setLoading(true);
    // Each failure keeps its own reason. Bulk never sends the paid-time override — an unpaid
    // request refused for that is approved anyway one at a time, from the request itself.
    const failures: BulkApproveFailure[] = [];
    for (const id of ids) {
      try {
        await API.approveTimeOffRequest(id);
      } catch (err) {
        const r = requests.find((x) => x.timeOffRequestID === id);
        failures.push({
          employeeName: r ? `${r.employeeName} (${r.categoryName})` : `Request #${id}`,
          message: approveFailureMessage(err, "Failed to approve."),
          code: err instanceof API.TimeOffActionError ? err.code : undefined,
        });
      }
    }
    const failureSummary = summarizeBulkApproveFailures(ids.length, failures);
    if (failureSummary) {
      createPopup(failureSummary.title, failureSummary.message);
    }
    setDetailCache((prev) => {
      const n = { ...prev };
      ids.forEach((id) => delete n[id]);
      return n;
    });
    setBalanceCache({});
    await loadRequests();
    await refreshCount();
  };

  // Client-side filtering
  const sq = searchQuery.toLowerCase();
  const matchingRequests = requests.filter((r) => {
    if (sq) {
      const matchName = r.employeeName.toLowerCase().includes(sq);
      const matchID   = (r.altEmployeeID ?? "").toLowerCase().includes(sq);
      if (!matchName && !matchID) return false;
    }
    if (categoryFilter && r.categoryName !== categoryFilter) return false;
    return true;
  });

  // Pending is a work queue and stays oldest-first (the API's SubmittedAt order). Every other
  // status is history, and payroll reads history newest-first: latest start date on top.
  const filteredRequests =
    statusFilter === "PENDING"
      ? matchingRequests
      : [...matchingRequests].sort(
          (a, b) =>
            b.startDate.localeCompare(a.startDate) ||
            String(b.submittedAt ?? "").localeCompare(String(a.submittedAt ?? ""))
        );

  const categories = Array.from(new Set(requests.map((r) => r.categoryName))).sort();

  // Pagination
  const totalItems  = filteredRequests.length;
  const totalPages  = Math.max(1, Math.ceil(totalItems / rowsPerPage));
  const safePage    = Math.min(currentPage, totalPages);
  const pageItems   = filteredRequests.slice((safePage - 1) * rowsPerPage, safePage * rowsPerPage);

  const pendingVisible     = requests.filter((r) => r.status === "PENDING" && canActOn(r));
  // Super admins always get the bulk column; everyone else gets it only when the API says at
  // least one loaded request is theirs to act on.
  const showBulk = isAdmin || requests.some((r) => r.canAct === true);
  const allPendingSelected =
    pendingVisible.length > 0 &&
    pendingVisible.every((r) => selectedIDs.has(r.timeOffRequestID));

  const toggleSelectAll = () => {
    if (allPendingSelected) setSelectedIDs(new Set());
    else setSelectedIDs(new Set(pendingVisible.map((r) => r.timeOffRequestID)));
  };

  const displayedPendingCount = pendingTimeOffCount ?? pendingVisible.length;

  return (
    <PageWrapper title="Time Off Approvals" backgroundColor="#f4f5f7" backArrow={false}>
      <div className="toa">

        {/* ── Page header ───────────────────────────────────────────────── */}
        <div className="toa-head">
          <div className="toa-head-logo">
            <Calendar size={23} />
          </div>
          <div className="toa-head-text">
            <h1 className="toa-head-title">Time Off Approvals</h1>
            <div className="toa-head-sub">Review and act on time-off requests for your team</div>
          </div>
          <div className="toa-head-stat toa-head-stat-pending">
            <span className="toa-head-stat-n">{displayedPendingCount}</span>
            <span className="toa-head-stat-l">Pending</span>
          </div>
        </div>

        {/* ── Tabs ──────────────────────────────────────────────────────── */}
        <div className="toa-tabs">
          <button
            className={`toa-tab${mainTab === "queue" ? " on" : ""}`}
            onClick={() => setMainTab("queue")}
          >
            <Inbox size={17} /> Approval Queue
          </button>
          <button
            className={`toa-tab${mainTab === "calendar" ? " on" : ""}`}
            onClick={() => setMainTab("calendar")}
          >
            <Calendar size={17} /> Calendar
          </button>
        </div>

        {/* ── Approval Queue ────────────────────────────────────────────── */}
        {mainTab === "queue" && (
          <div className="toa-queue">

            {/* Request-type segmented control */}
            {SHOW_CASH_OUT_TAB && (
              <div className="toa-reqseg">
                <button
                  className={queueSubTab === "timeOff" ? "on" : ""}
                  onClick={() => setQueueSubTab("timeOff")}
                >
                  <Inbox size={15} /> Time Off Requests
                </button>
                <button
                  className={queueSubTab === "cashOut" ? "on" : ""}
                  onClick={() => setQueueSubTab("cashOut")}
                >
                  <DollarSign size={15} /> Cash Out Requests
                </button>
              </div>
            )}

            {queueSubTab === "timeOff" && (
              <>
                {/* Filter bar */}
                <div className="toa-filterbar">
                  <div className="toa-field toa-field-search">
                    <span className="toa-flab">Search</span>
                    <div className="toa-searchwrap">
                      <Search size={17} className="toa-search-icon" />
                      <input
                        type="text"
                        className="toa-input"
                        placeholder="Search by name or Employee ID…"
                        value={searchQuery}
                        onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                      />
                    </div>
                  </div>
                  <div className="toa-field">
                    <span className="toa-flab">Status</span>
                    <select
                      className="toa-select"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as TimeOffRequestStatus | "ALL")}
                    >
                      <option value="PENDING">Pending</option>
                      <option value="APPROVED">Approved</option>
                      <option value="DENIED">Denied</option>
                      <option value="CANCELLED">Cancelled</option>
                      <option value="ALL">All</option>
                    </select>
                  </div>
                  <div className="toa-field">
                    <span className="toa-flab">Category</span>
                    <select
                      className="toa-select"
                      value={categoryFilter}
                      onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
                    >
                      <option value="">All categories</option>
                      {categories.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div className="toa-field">
                    <span className="toa-flab">Date range</span>
                    <div className="toa-date-pair">
                      <input
                        type="date"
                        className="toa-input toa-input-date"
                        value={startDateFilter}
                        onChange={(e) => setStartDateFilter(e.target.value)}
                      />
                      <span className="toa-date-sep">→</span>
                      <input
                        type="date"
                        className="toa-input toa-input-date"
                        value={endDateFilter}
                        onChange={(e) => setEndDateFilter(e.target.value)}
                      />
                    </div>
                  </div>
                  <button className="toa-btn" onClick={loadRequests}>
                    <RefreshCw size={16} /> Refresh
                  </button>
                  {allowSubmitForEmployee && (
                    <button
                      className="toa-btn toa-btn-primary"
                      onClick={() => setNewRequestOpen(true)}
                    >
                      <Plus size={16} /> New request
                    </button>
                  )}
                </div>

                {/* Result meta */}
                {!loading && (
                  <div className="toa-result-meta">
                    Showing{" "}
                    <b>{totalItems}</b>{" "}
                    {statusFilter === "PENDING" ? "pending " : ""}
                    request{totalItems !== 1 ? "s" : ""}
                    {clearedCount > 0 && (
                      <span className="toa-cleared">
                        {" · "}<b>{clearedCount}</b> cleared this session
                      </span>
                    )}
                    <span className="toa-meta-spacer" />
                    <span className="toa-sortbtn">Sorted by Submitted (newest)</span>
                  </div>
                )}

                {/* Admin bulk bar */}
                {showBulk && selectedIDs.size > 0 && (
                  <div className="toa-bulk-bar">
                    <span>{selectedIDs.size} selected</span>
                    <button className="toa-btn toa-btn-approve-solid" onClick={handleBulkApprove}>
                      <Check size={14} /> Approve Selected
                    </button>
                    <button className="toa-btn" onClick={() => setSelectedIDs(new Set())}>
                      Clear
                    </button>
                  </div>
                )}

                {/* Table or empty state */}
                {loading ? (
                  <div className="toa-loading">Loading requests…</div>
                ) : filteredRequests.length === 0 ? (
                  <div className="toa-empty">
                    <CheckCircle size={34} />
                    <div className="toa-empty-title">You're all caught up</div>
                    <div className="toa-empty-sub">
                      No requests match your current filters.
                    </div>
                  </div>
                ) : (
                  <div className="toa-qcard">
                    <table className="toa-qtable">
                      <thead>
                        <tr>
                          {showBulk && (
                            <th className="toa-td-check">
                              {pendingVisible.length > 0 && (
                                <input
                                  type="checkbox"
                                  checked={allPendingSelected}
                                  onChange={toggleSelectAll}
                                  title="Select all pending"
                                />
                              )}
                            </th>
                          )}
                          <th>Employee</th>
                          <th className="toa-col-id">ID</th>
                          <th>Category</th>
                          <th>Dates</th>
                          <th className="toa-num">Hours</th>
                          <th className="toa-col-sub">Submitted</th>
                          <th>Status</th>
                          <th className="toa-acts">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pageItems.map((req) => (
                          <RequestRow
                            key={req.timeOffRequestID}
                            req={req}
                            showBulk={showBulk}
                            expanded={expandedID === req.timeOffRequestID}
                            detail={detailCache[req.timeOffRequestID] ?? null}
                            loadingDetail={loadingDetailID === req.timeOffRequestID}
                            selected={selectedIDs.has(req.timeOffRequestID)}
                            removing={removingIDs.has(req.timeOffRequestID)}
                            detailNote={detailNotes[req.timeOffRequestID] ?? ""}
                            balances={balanceCache[req.employeeID] ?? null}
                            loadingBalances={loadingBalEmpID === req.employeeID}
                            balancesError={balanceErrEmpIDs.has(req.employeeID)}
                            allowEditApproved={allowEditApproved}
                            onRetryBalances={() => loadBalances(req.employeeID, true)}
                            onToggleExpand={() => handleToggleExpand(req.timeOffRequestID)}
                            onToggleSelect={() => handleToggleSelect(req.timeOffRequestID)}
                            onApprove={(r, note) => {
                              // The 8-hour warning lives on the detail, so pull it even when the
                              // approve came from the collapsed row's quick button.
                              loadDetail(r.timeOffRequestID);
                              setActionModal({ action: "approve", req: r, initialNote: note });
                            }}
                            onDeny={(r, note) =>
                              setActionModal({ action: "deny", req: r, initialNote: note })
                            }
                            onModifyHours={(r) => setModifyModal(r)}
                            onDenyApproved={(r) =>
                              setActionModal({ action: "denyApproved", req: r })
                            }
                            onDetailNoteChange={(note) =>
                              setDetailNotes((prev) => ({
                                ...prev,
                                [req.timeOffRequestID]: note,
                              }))
                            }
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="toa-pager">
                    <span className="toa-pager-count">
                      Page <b>{safePage}</b> of <b>{totalPages}</b>
                    </span>
                    <div className="toa-per-page">
                      Rows per page
                      <select
                        className="toa-select"
                        value={rowsPerPage}
                        onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>
                    </div>
                    <div className="toa-pages">
                      <button
                        className="toa-pg toa-pg-nav"
                        onClick={() => setCurrentPage(1)}
                        disabled={safePage === 1}
                      >
                        <ChevronLeft size={16} /><ChevronLeft size={16} />
                      </button>
                      <button
                        className="toa-pg toa-pg-nav"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={safePage === 1}
                      >
                        <ChevronLeft size={16} />
                      </button>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const pg =
                          safePage <= 3            ? i + 1 :
                          safePage >= totalPages - 2 ? totalPages - 4 + i :
                          safePage - 2 + i;
                        if (pg < 1 || pg > totalPages) return null;
                        return (
                          <button
                            key={pg}
                            className={`toa-pg${pg === safePage ? " on" : ""}`}
                            onClick={() => setCurrentPage(pg)}
                          >
                            {pg}
                          </button>
                        );
                      })}
                      <button
                        className="toa-pg toa-pg-nav"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={safePage === totalPages}
                      >
                        <ChevronRight size={16} />
                      </button>
                      <button
                        className="toa-pg toa-pg-nav"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={safePage === totalPages}
                      >
                        <ChevronRight size={16} /><ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {queueSubTab === "cashOut" && (
              <CashOutQueue onCountChange={refreshCount} />
            )}
          </div>
        )}

        {/* ── Calendar ──────────────────────────────────────────────────── */}
        {mainTab === "calendar" && (
          <CalendarView onCountChange={refreshCount} />
        )}

        {/* ── Confirm modal ─────────────────────────────────────────────── */}
        {actionModal && (
          <ActionModal
            action={actionModal.action}
            request={actionModal.req}
            initialNote={actionModal.initialNote}
            detail={detailCache[actionModal.req.timeOffRequestID] ?? null}
            onConfirm={handleAction}
            onClose={() => setActionModal(null)}
          />
        )}

        {/* ── Modify hours ──────────────────────────────────────────────── */}
        {modifyModal && detailCache[modifyModal.timeOffRequestID] && (
          <ModifyHoursModal
            request={modifyModal}
            detail={detailCache[modifyModal.timeOffRequestID]}
            balances={balanceCache[modifyModal.employeeID] ?? null}
            loadingBalances={loadingBalEmpID === modifyModal.employeeID}
            balancesError={balanceErrEmpIDs.has(modifyModal.employeeID)}
            onRetryBalances={() => loadBalances(modifyModal.employeeID, true)}
            onSaved={(totalHours) => handleHoursModified(modifyModal, totalHours)}
            onClose={() => setModifyModal(null)}
          />
        )}

        {/* ── Paid-time override (Unpaid Time) ──────────────────────────── */}
        {approveOverride && (
          <PaidOverrideModal
            mode="approve"
            employeeName={approveOverride.req.employeeName}
            error={approveOverride.error}
            onConfirm={handleApproveOverride}
            onClose={() => setApproveOverride(null)}
          />
        )}

        {/* ── New request (on an employee's behalf) ─────────────────────── */}
        {newRequestOpen && (
          <NewTimeOffRequestModal
            onSubmitted={handleNewRequestSubmitted}
            onClose={() => setNewRequestOpen(false)}
          />
        )}
      </div>
    </PageWrapper>
  );
};

export default TimeOffRequestApprovalPage;
